export const gettingStartedMap: { [key: string]: string } = {
  'getting-started': `
  <div class="getting-started-container">
    <div class="page-header">
      <h1>Getting Started with XPeer.AI</h1>
      <p class="page-subtitle">Your comprehensive guide to intelligent automation platform</p>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🏗️</span>
        XPeer.AI Architecture
      </h2>
      <div class="architecture-cards">
        <div class="arch-card">
          <div class="card-icon">🌐</div>
          <h3>Web Application</h3>
          <p>The main platform for managing and executing automation workflows</p>
        </div>
        <div class="arch-card">
          <div class="card-icon">📹</div>
          <h3>Recorder (Google Chrome Extension)</h3>
          <p>Captures user flows step by step and transfers them to the Canvas</p>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">⚡</span>
        How It Works
      </h2>
      <div class="workflow-container">
        <div class="workflow-item">
          <div class="workflow-number">1</div>
          <div class="workflow-content">
            <h4>Capture</h4>
            <p>The Recorder captures user flows step by step</p>
          </div>
        </div>
        <div class="workflow-separator">→</div>
        <div class="workflow-item">
          <div class="workflow-number">2</div>
          <div class="workflow-content">
            <h4>Transfer</h4>
            <p>Steps are transferred to the Canvas within the Web Application</p>
          </div>
        </div>
        <div class="workflow-separator">→</div>
        <div class="workflow-item">
          <div class="workflow-number">3</div>
          <div class="workflow-content">
            <h4>Test & Validate</h4>
            <p>Test and validate recorded steps in Canvas</p>
          </div>
        </div>
        <div class="workflow-separator">→</div>
        <div class="workflow-item">
          <div class="workflow-number">4</div>
          <div class="workflow-content">
            <h4>Execute</h4>
            <p>Execute from Canvas or move to Library for permanent storage</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🚪</span>
        Web Application Portals
      </h2>
      <div class="portals-container">
        <div class="portal-item canvas">
          <div class="portal-icon">🖼️</div>
          <h3>Canvas</h3>
          <p>Temporary workspace for testing and executing recorded steps</p>
          <span class="portal-tag">Testing</span>
        </div>
        <div class="portal-item library">
          <div class="portal-icon">📚</div>
          <h3>Library</h3>
          <p>Permanent storage for stable, reusable flows</p>
          <span class="portal-tag">Storage</span>
        </div>
        <div class="portal-item">
          <div class="portal-icon">📁</div>
          <h3>Project</h3>
          <p>Project management and organization</p>
        </div>
        <div class="portal-item">
          <div class="portal-icon">🎨</div>
          <h3>Studio</h3>
          <p>Advanced editing and configuration</p>
        </div>
        <div class="portal-item report">
          <div class="portal-icon">📊</div>
          <h3>Report</h3>
          <p>Execution results, analytics, and detailed insights</p>
          <span class="portal-tag">Analytics</span>
        </div>
        <div class="portal-item">
          <div class="portal-icon">⚙️</div>
          <h3>Setting</h3>
          <p>System configuration and user preferences</p>
        </div>
        <div class="portal-item">
          <div class="portal-icon">🔍</div>
          <h3>Code Review</h3>
          <p>Code analysis and review tools for automation scripts</p>
        </div>
        <div class="portal-item">
          <div class="portal-icon">💼</div>
          <h3>Business Review</h3>
          <p>Business process analysis and workflow optimization</p>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🏛️</span>
        Library Organization Structure
      </h2>
      <div class="hierarchy-container">
        <div class="hierarchy-item">
          <div class="hierarchy-icon">📦</div>
          <div class="hierarchy-content">
            <h3>Suites</h3>
            <p>Top-level containers for related test collections</p>
          </div>
        </div>
        <div class="hierarchy-arrow">↓</div>
        <div class="hierarchy-item">
          <div class="hierarchy-icon">🎯</div>
          <div class="hierarchy-content">
            <h3>Features</h3>
            <p>Grouped scenarios representing specific functionality</p>
          </div>
        </div>
        <div class="hierarchy-arrow">↓</div>
        <div class="hierarchy-item">
          <div class="hierarchy-icon">📋</div>
          <div class="hierarchy-content">
            <h3>Scenarios</h3>
            <p>Individual test cases or user journeys</p>
          </div>
        </div>
        <div class="hierarchy-arrow">↓</div>
        <div class="hierarchy-item">
          <div class="hierarchy-icon">⚙️</div>
          <div class="hierarchy-content">
            <h3>Step</h3>
            <p>Individual automation steps</p>
          </div>
        </div>
      </div>
      <div class="hierarchy-note">
        <p><strong>Note:</strong> Steps or Scenarios may also be directly added to a Suite.</p>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">▶️</span>
        Execution Panel
      </h2>
      <div class="execution-info">
        <p>In the Execution Panel, only Suites can be executed. The panel provides several configuration options to customize the execution:</p>
        
        <div class="execution-option">
          <h4>Browser Selection</h4>
          <p>Choose the browser on which the Suite will run (e.g., Chrome, Firefox, Opera).</p>
        </div>
        
        <div class="execution-option">
          <h4>Delay Settings</h4>
          <p>Add a delay (in seconds) before each action.</p>
          <p>This is useful for websites that load slowly.</p>
          <p>Without a delay, fast execution may cause failures (e.g., "Element not found" errors if the page has not fully loaded).</p>
          <p>Delay ensures elements are available before the next step is executed.</p>
        </div>
        
        <div class="execution-option">
          <h4>Email Notifications</h4>
          <p>Option to send a notification after execution completes.</p>
          <p>Notifications can only be sent to registered email IDs associated with the account.</p>
        </div>
        
        <div class="execution-option">
          <h4>Visual Comparison</h4>
          <p>Enables detection of UI changes between runs.</p>
          <p>After re-execution, XPeer.AI highlights differences in the user interface.</p>
          <p>This allows teams to verify if any unintended UI changes have occurred.</p>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">📊</span>
        Report Portal Features
      </h2>
      <div class="report-features">
        <div class="report-item">
          <div class="report-icon">📈</div>
          <h4>Statistics Report</h4>
          <p>Overall execution summary</p>
        </div>
        <div class="report-item">
          <div class="report-icon">📋</div>
          <h4>Step-wise Report</h4>
          <p>Detailed breakdown of each executed step</p>
        </div>
        <div class="report-item">
          <div class="report-icon">📹</div>
          <h4>Live Video</h4>
          <p>Real-time execution monitoring</p>
        </div>
        <div class="report-item">
          <div class="report-icon">🎬</div>
          <h4>Recorded Video</h4>
          <p>Playback of completed runs</p>
        </div>
        <div class="report-item">
          <div class="report-icon">📝</div>
          <h4>Logs</h4>
          <p>Detailed execution logs for debugging</p>
        </div>
        <div class="report-item">
          <div class="report-icon">📊</div>
          <h4>Dataset Reports</h4>
          <p>Data-driven execution details</p>
        </div>
      </div>
    </div>
    
    <div class="important-notice">
      <div class="notice-icon">💡</div>
      <div class="notice-text">
        <strong>Key Points:</strong> Automation can be executed from both Canvas (for quick testing) and Library (for stable flows). The Canvas is intended for quick testing, while the Library is used for running stable, reusable flows.
      </div>
    </div>
  </div>
  
  <style>
  .getting-started-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 30px 0;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
    border-radius: 15px;
  }
  
  .page-header h1 {
    font-size: 2.5em;
    margin: 0 0 10px 0;
    font-weight: 700;
    color: white;
  }
  
  .page-subtitle {
    font-size: 1.2em;
    opacity: 0.9;
    margin: 0;
  }
  
  .content-section {
    margin-bottom: 40px;
    background: white;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    font-size: 1.3rem;
    color: #495057;
    font-weight: 600;
  }
  
  .title-icon {
    font-size: 1.2rem;
    margin-right: 0.75rem;
  }
  
  .architecture-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }
  
  .arch-card {
    padding: 1.25rem;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 3px solid #7b1fa2;
    text-align: left;
  }
  
  .card-icon {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
  
  .arch-card h3 {
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  .arch-card p {
    color: #6c757d;
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .workflow-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  
  .workflow-item {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    min-width: 180px;
    min-height: 5rem;
    flex: 1;
  }
  
  .workflow-number {
    background: #7b1fa2;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.8rem;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }
  
  .workflow-content h4 {
    margin: 0 0 0.25rem 0;
    font-size: 0.9rem;
    color: #495057;
  }
  
  .workflow-content p {
    margin: 0;
    font-size: 0.8rem;
    color: #6c757d;
    line-height: 1.3;
  }
  
  .workflow-separator {
    font-size: 1.2rem;
    color: #7b1fa2;
    font-weight: bold;
    margin: 0 0.5rem;
    display:none;
  }
  
  .portals-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .portal-item {
    padding: 1.25rem;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    text-align: center;
    position: relative;
    background: #fff;
    transition: all 0.2s ease;
  }
  
  .portal-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .portal-item.primary {
    border-color: #28a745;
    background: linear-gradient(135deg, #f8fff9 0%, #e8f5e8 100%);
  }
  
  .portal-item.report {
    border-color: #007bff;
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
  }
  
  .portal-icon {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
  
  .portal-item h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: #495057;
  }
  
  .portal-item p {
    margin: 0;
    font-size: 0.85rem;
    color: #6c757d;
    line-height: 1.3;
  }
  
  .portal-tag {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: #28a745;
    color: white;
    padding: 0.2rem 0.5rem;
    border-radius: 8px;
    font-size: 0.7rem;
    font-weight: bold;
  }
  
  .hierarchy-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
  
  .hierarchy-item {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    width: 100%;
    max-width: 350px;
  }
  
  .hierarchy-icon {
    font-size: 1.5rem;
    margin-right: 1rem;
  }
  
  .hierarchy-content h3 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    color: #495057;
  }
  
  .hierarchy-content p {
    margin: 0;
    font-size: 0.85rem;
    color: #6c757d;
    line-height: 1.3;
  }
  
  .hierarchy-arrow {
    font-size: 1.2rem;
    color: #7b1fa2;
    font-weight: bold;
  }
  
  .quick-start-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
  }
  
  .quick-item {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    text-align: center;
  }
  
  .quick-icon {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }
  
  .quick-item h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.9rem;
    color: #495057;
  }
  
  .quick-item p {
    margin: 0;
    font-size: 0.8rem;
    color: #6c757d;
    line-height: 1.3;
  }
  
  .important-notice {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    border: 1px solid #ffc107;
    border-radius: 6px;
    margin-top: 1.5rem;
  }
  
  .notice-icon {
    font-size: 1.2rem;
    margin-right: 0.75rem;
    margin-top: 0.1rem;
  }
  
  .notice-text {
    color: #856404;
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .notice-text strong {
    color: #856404;
  }
  
  .hierarchy-note {
    margin-top: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 4px;
    border-left: 3px solid #7b1fa2;
  }
  
  .hierarchy-note p {
    margin: 0;
    font-size: 0.85rem;
    color: #495057;
  }
  
  .execution-info {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .execution-option {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid #667eea;
  }
  
  .execution-option h4 {
    color: #333;
    margin-bottom: 10px;
    font-size: 1.2em;
    font-weight: 600;
  }
  
  .execution-option p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 8px;
  }
  
  .execution-option p:last-child {
    margin-bottom: 0;
  }
  
  .execution-card {
    display: flex;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 8px;
    border: 1px solid #e9ecef;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    transition: all 0.2s ease;
  }
  
  .execution-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  
  .exec-icon {
    font-size: 2rem;
    margin-right: 1.25rem;
    padding: 0.75rem;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    flex-shrink: 0;
  }
  
  .execution-card h3 {
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  .execution-card p {
    color: #6c757d;
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  .report-features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .report-item {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    text-align: center;
  }
  
  .report-icon {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }
  
  .report-item h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.9rem;
    color: #495057;
  }
  
  .report-item p {
    margin: 0;
    font-size: 0.8rem;
    color: #6c757d;
    line-height: 1.3;
  }
  
  @media (max-width: 768px) {
    .workflow-container {
      flex-direction: column;
    }
    
    .workflow-separator {
      transform: rotate(90deg);
    }
    
    .architecture-cards, .portals-container, .execution-info, .report-features {
      grid-template-columns: 1fr;
    }
  }
  </style>
`,
  'installation': `
  <div class="installation-container">
    <div class="page-header">
      <h1>Installation Guide</h1>
      <p class="page-subtitle">Get XPeer.AI up and running in minutes</p>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🌐</span>
        Web Application Access
      </h2>
      <div class="installation-steps">
        <div class="step-item">
          <div class="step-number">1</div>
          <div class="step-content">
            <h4>Access the Platform</h4>
            <p>Navigate to the XPeer.AI web application using your browser</p>
            <div class="code-block">
              <code>https://xpeer.ai</code>
            </div>
          </div>
        </div>
        <div class="step-item">
          <div class="step-number">2</div>
          <div class="step-content">
            <h4>Create Account</h4>
            <p>Sign up for a new account or log in with existing credentials</p>
          </div>
        </div>
        <div class="step-item">
          <div class="step-number">3</div>
          <div class="step-content">
            <h4>Verify Access</h4>
            <p>Ensure you can access the Canvas, Library, and other portals</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">📹</span>
        Chrome Extension Installation
      </h2>
      <div class="extension-installation">
        <div class="install-method">
          <h3>Method 1: Chrome Web Store</h3>
          <ol>
            <li>Open Google Chrome browser</li>
            <li>Navigate to Chrome Web Store</li>
            <li>Search for "XPeer.AI Recorder"</li>
            <li>Click "Add to Chrome"</li>
            <li>Confirm installation when prompted</li>
          </ol>
        </div>
        
        <div class="install-method">
          <h3>Method 2: Manual Installation</h3>
          <ol>
            <li>Download the extension package (.crx file)</li>
            <li>Open Chrome and go to <code>chrome://extensions/</code></li>
            <li>Enable "Developer mode" in the top right</li>
            <li>Click "Load unpacked" and select the extension folder</li>
            <li>The extension will appear in your extensions list</li>
          </ol>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">✅</span>
        Verification Steps
      </h2>
      <div class="verification-checklist">
        <div class="checklist-item">
          <span class="check-icon">✓</span>
          <span>Web application loads successfully</span>
        </div>
        <div class="checklist-item">
          <span class="check-icon">✓</span>
          <span>Chrome extension icon appears in toolbar</span>
        </div>
        <div class="checklist-item">
          <span class="check-icon">✓</span>
          <span>Extension can capture user interactions</span>
        </div>
        <div class="checklist-item">
          <span class="check-icon">✓</span>
          <span>Recorded steps transfer to Canvas</span>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🔧</span>
        System Requirements
      </h2>
      <div class="requirements-grid">
        <div class="requirement-card">
          <h4>Browser</h4>
          <ul>
            <li>Google Chrome (recommended)</li>
            <li>Mozilla Firefox</li>
            <li>Microsoft Edge</li>
            <li>Safari (limited support)</li>
          </ul>
        </div>
        <div class="requirement-card">
          <h4>Operating System</h4>
          <ul>
            <li>Windows 10/11</li>
            <li>macOS 10.14+</li>
            <li>Linux (Ubuntu 18.04+)</li>
          </ul>
        </div>
        <div class="requirement-card">
          <h4>Network</h4>
          <ul>
            <li>Stable internet connection</li>
            <li>HTTPS support</li>
            <li>Port 443 access</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong> 
      <span>Make sure to allow pop-ups and enable JavaScript for the best experience with XPeer.AI.</span>
    </div>
  </div>
  
  <style>
  .installation-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 30px 0;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
    border-radius: 15px;
  }
  
  .page-header h1 {
    font-size: 2.5em;
    margin: 0 0 10px 0;
    font-weight: 700;
    color: white;
  }
  
  .page-subtitle {
    font-size: 1.2em;
    opacity: 0.9;
    margin: 0;
  }
  
  .content-section {
    margin-bottom: 40px;
    background: white;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .section-title {
    display: flex;
    align-items: center;
    font-size: 1.8em;
    color: #333;
    margin-bottom: 25px;
    border-bottom: 3px solid #667eea;
    padding-bottom: 10px;
  }
  
  .title-icon {
    font-size: 1.2em;
    margin-right: 15px;
  }
  
  .installation-steps {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .step-item {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 10px;
    border-left: 4px solid #667eea;
  }
  
  .step-number {
    background: #667eea;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  .step-content h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1.3em;
  }
  
  .step-content p {
    margin: 0 0 15px 0;
    color: #666;
    line-height: 1.6;
  }
  
  .code-block {
    background: #2d3748;
    color: #e2e8f0;
    padding: 15px;
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    margin-top: 10px;
  }
  
  .code-block code {
    font-size: 1.1em;
  }
  
  .extension-installation {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  
  .install-method {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border: 2px solid #e9ecef;
  }
  
  .install-method h3 {
    color: #333;
    margin-bottom: 20px;
    font-size: 1.4em;
  }
  
  .install-method ol {
    margin: 0;
    padding-left: 20px;
  }
  
  .install-method li {
    margin-bottom: 10px;
    color: #666;
    line-height: 1.6;
  }
  
  .verification-checklist {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  
  .checklist-item {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px;
    background: #e8f5e8;
    border-radius: 8px;
    border-left: 4px solid #28a745;
  }
  
  .check-icon {
    color: #28a745;
    font-weight: bold;
    font-size: 1.2em;
  }
  
  .requirements-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;
  }
  
  .requirement-card {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border: 2px solid #e9ecef;
  }
  
  .requirement-card h4 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.3em;
  }
  
  .requirement-card ul {
    margin: 0;
    padding-left: 20px;
  }
  
  .requirement-card li {
    margin-bottom: 8px;
    color: #666;
    line-height: 1.5;
  }
  
  .blue-note {
    background: #e3f2fd;
    border: 1px solid #2196f3;
    border-radius: 8px;
    padding: 20px;
    margin-top: 30px;
    display: flex;
    align-items: flex-start;
    gap: 15px;
  }
  
  .blue-note .icon {
    color: #2196f3;
    font-size: 1.2em;
    margin-top: 2px;
  }
  
  .blue-note strong {
    color: #1976d2;
  }
  
  .blue-note span {
    color: #1565c0;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    .extension-installation {
      grid-template-columns: 1fr;
    }
    
    .verification-checklist {
      grid-template-columns: 1fr;
    }
    
    .requirements-grid {
      grid-template-columns: 1fr;
    }
  }
  </style>
`,
  'best-practices': `
  <div class="best-practices-container">
    <div class="page-header">
      <h1>Best Practices</h1>
      <p class="page-subtitle">I'm in best practices menu - Essential guidelines for effective automation</p>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🎯</span>
        Planning & Design
      </h2>
      <div class="practices-grid">
        <div class="practice-card">
          <div class="practice-icon">📋</div>
          <h3>Define Clear Objectives</h3>
          <p>Before creating automation flows, clearly define what you want to achieve and the expected outcomes.</p>
          <ul>
            <li>Identify the specific business process</li>
            <li>Set measurable success criteria</li>
            <li>Document expected results</li>
          </ul>
        </div>
        
        <div class="practice-card">
          <div class="practice-icon">🏗️</div>
          <h3>Modular Design</h3>
          <p>Break down complex processes into smaller, reusable components for better maintainability.</p>
          <ul>
            <li>Create reusable action blocks</li>
            <li>Use consistent naming conventions</li>
            <li>Implement error handling at each level</li>
          </ul>
        </div>
        
        <div class="practice-card">
          <div class="practice-icon">🔍</div>
          <h3>Element Selection Strategy</h3>
          <p>Choose robust selectors that won't break with UI changes.</p>
          <ul>
            <li>Prefer stable attributes (ID, data-testid)</li>
            <li>Avoid position-based selectors</li>
            <li>Use CSS selectors over XPath when possible</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">⚡</span>
        Performance Optimization
      </h2>
      <div class="performance-tips">
        <div class="tip-item">
          <div class="tip-number">1</div>
          <div class="tip-content">
            <h4>Use Explicit Waits</h4>
            <p>Instead of fixed delays, use explicit waits for elements to be ready</p>
            <div class="code-example">
              <code>wait-for-element</code> instead of <code>pause</code>
            </div>
          </div>
        </div>
        
        <div class="tip-item">
          <div class="tip-number">2</div>
          <div class="tip-content">
            <h4>Minimize Browser Actions</h4>
            <p>Reduce unnecessary browser interactions to improve execution speed</p>
            <div class="code-example">
              Use <code>enter-text</code> directly instead of <code>click</code> + <code>enter-text</code>
            </div>
          </div>
        </div>
        
        <div class="tip-item">
          <div class="tip-number">3</div>
          <div class="tip-content">
            <h4>Optimize Data Handling</h4>
            <p>Process data efficiently and avoid redundant operations</p>
            <div class="code-example">
              Use <code>set-value</code> for bulk data operations
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🛡️</span>
        Error Handling & Reliability
      </h2>
      <div class="reliability-checklist">
        <div class="checklist-section">
          <h3>Pre-execution Checks</h3>
          <div class="checklist-items">
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Verify application state before starting</span>
            </div>
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Check for required data availability</span>
            </div>
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Validate environment configuration</span>
            </div>
          </div>
        </div>
        
        <div class="checklist-section">
          <h3>During Execution</h3>
          <div class="checklist-items">
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Implement try-catch logic for critical steps</span>
            </div>
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Use conditional logic for dynamic scenarios</span>
            </div>
            <div class="check-item">
              <span class="check-icon">✓</span>
              <span>Add verification steps at key points</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">📊</span>
        Testing & Validation
      </h2>
      <div class="testing-guidelines">
        <div class="guideline-card">
          <h3>Test Data Management</h3>
          <ul>
            <li>Use separate test data for different environments</li>
            <li>Implement data cleanup after test execution</li>
            <li>Use realistic test scenarios</li>
          </ul>
        </div>
        
        <div class="guideline-card">
          <h3>Assertion Strategy</h3>
          <ul>
            <li>Verify both positive and negative scenarios</li>
            <li>Test boundary conditions</li>
            <li>Validate business logic, not just UI elements</li>
          </ul>
        </div>
        
        <div class="guideline-card">
          <h3>Reporting & Documentation</h3>
          <ul>
            <li>Include meaningful test descriptions</li>
            <li>Document expected vs actual results</li>
            <li>Maintain execution logs for debugging</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="blue-note">
      <span class="icon">💡</span>
      <strong>Pro Tip:</strong> 
      <span>Start with simple, stable flows and gradually add complexity. Always test in a safe environment before deploying to production.</span>
    </div>
  </div>
  
  <style>
  .best-practices-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 30px 0;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
    border-radius: 15px;
  }
  
  .page-header h1 {
    font-size: 2.5em;
    margin: 0 0 10px 0;
    font-weight: 700;
    color: white;
  }
  
  .page-subtitle {
    font-size: 1.2em;
    opacity: 0.9;
    margin: 0;
  }
  
  .content-section {
    margin-bottom: 40px;
    background: white;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .section-title {
    display: flex;
    align-items: center;
    font-size: 1.8em;
    color: #333;
    margin-bottom: 25px;
    border-bottom: 3px solid #667eea;
    padding-bottom: 10px;
  }
  
  .title-icon {
    font-size: 1.2em;
    margin-right: 15px;
  }
  
  .practices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 25px;
  }
  
  .practice-card {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 12px;
    border-left: 4px solid #667eea;
    transition: transform 0.2s ease;
  }
  
  .practice-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  .practice-icon {
    font-size: 2em;
    margin-bottom: 15px;
  }
  
  .practice-card h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.4em;
  }
  
  .practice-card p {
    color: #666;
    margin-bottom: 15px;
    line-height: 1.6;
  }
  
  .practice-card ul {
    margin: 0;
    padding-left: 20px;
  }
  
  .practice-card li {
    margin-bottom: 8px;
    color: #555;
    line-height: 1.5;
  }
  
  .performance-tips {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .tip-item {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 10px;
    border-left: 4px solid #28a745;
  }
  
  .tip-number {
    background: #28a745;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  .tip-content h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1.3em;
  }
  
  .tip-content p {
    margin: 0 0 15px 0;
    color: #666;
    line-height: 1.6;
  }
  
  .code-example {
    background: #2d3748;
    color: #e2e8f0;
    padding: 12px;
    border-radius: 6px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }
  
  .reliability-checklist {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  
  .checklist-section h3 {
    color: #333;
    margin-bottom: 20px;
    font-size: 1.4em;
  }
  
  .checklist-items {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .check-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #e8f5e8;
    border-radius: 8px;
    border-left: 4px solid #28a745;
  }
  
  .check-icon {
    color: #28a745;
    font-weight: bold;
    font-size: 1.1em;
  }
  
  .testing-guidelines {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;
  }
  
  .guideline-card {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border: 2px solid #e9ecef;
  }
  
  .guideline-card h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.3em;
  }
  
  .guideline-card ul {
    margin: 0;
    padding-left: 20px;
  }
  
  .guideline-card li {
    margin-bottom: 8px;
    color: #666;
    line-height: 1.5;
  }
  
  .blue-note {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 20px;
    margin-top: 30px;
    display: flex;
    align-items: flex-start;
    gap: 15px;
  }
  
  .blue-note .icon {
    color: #f39c12;
    font-size: 1.2em;
    margin-top: 2px;
  }
  
  .blue-note strong {
    color: #e67e22;
  }
  
  .blue-note span {
    color: #d35400;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    .practices-grid {
      grid-template-columns: 1fr;
    }
    
    .reliability-checklist {
      grid-template-columns: 1fr;
    }
    
    .testing-guidelines {
      grid-template-columns: 1fr;
    }
  }
  </style>
`,
  'what-is': `
  <div class="what-is-container">
    <div class="page-header">
      <h1>What is XPeer.AI?</h1>
      <p class="page-subtitle">I'm in XPeer.AI details - Discover the intelligent automation platform</p>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🤖</span>
        Platform Overview
      </h2>
      <div class="overview-content">
        <p class="lead-text">
          XPeer.AI is an intelligent automation platform designed to simplify and accelerate software development and testing workflows. It empowers users to visually create, manage, and execute end-to-end automation for both UI and API processes—without the need for complex scripting.
        </p>
        
        <div class="key-features">
          <div class="feature-card">
            <div class="feature-icon">🎨</div>
            <h3>Visual Interface</h3>
            <p>Create automation flows using an intuitive drag-and-drop interface</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🔗</div>
            <h3>Seamless Integration</h3>
            <p>Connect with GitHub, Jira, Slack, and other popular development tools</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>Smart Components</h3>
            <p>Pre-built automation components for common testing scenarios</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">📊</div>
            <h3>Analytics & Reporting</h3>
            <p>Comprehensive insights and detailed execution reports</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🏗️</span>
        Architecture Components
      </h2>
      <div class="architecture-overview">
        <div class="component-item">
          <div class="component-icon">🌐</div>
          <div class="component-details">
            <h3>Web Application</h3>
            <p>The main platform where users design, manage, and execute automation workflows. Features include Canvas for testing, Library for storage, Project management, Studio for advanced editing, and comprehensive reporting.</p>
            <ul>
              <li>Canvas - Temporary workspace for testing</li>
              <li>Library - Permanent storage for reusable flows</li>
              <li>Project - Organization and management</li>
              <li>Studio - Advanced configuration</li>
              <li>Report - Analytics and insights</li>
            </ul>
          </div>
        </div>
        
        <div class="component-item">
          <div class="component-icon">📹</div>
          <div class="component-details">
            <h3>Chrome Extension (Recorder)</h3>
            <p>A powerful browser extension that captures user interactions and automatically converts them into automation steps. This eliminates the need for manual scripting and makes automation accessible to non-technical users.</p>
            <ul>
              <li>Real-time interaction capture</li>
              <li>Automatic step generation</li>
              <li>Seamless transfer to Canvas</li>
              <li>Cross-tab functionality</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🔄</span>
        How It Works
      </h2>
      <div class="workflow-steps">
        <div class="workflow-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h4>Record</h4>
            <p>Use the Chrome extension to record your interactions with web applications</p>
          </div>
        </div>
        
        <div class="workflow-arrow">→</div>
        
        <div class="workflow-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h4>Transfer</h4>
            <p>Recorded steps are automatically transferred to the Canvas in the web application</p>
          </div>
        </div>
        
        <div class="workflow-arrow">→</div>
        
        <div class="workflow-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h4>Test & Refine</h4>
            <p>Test and refine your automation flows in the Canvas environment</p>
          </div>
        </div>
        
        <div class="workflow-arrow">→</div>
        
        <div class="workflow-step">
          <div class="step-number">4</div>
          <div class="step-content">
            <h4>Execute & Store</h4>
            <p>Execute flows directly or save them to the Library for future use</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🎯</span>
        Key Benefits
      </h2>
      <div class="benefits-grid">
        <div class="benefit-item">
          <div class="benefit-icon">⏱️</div>
          <h3>Time Savings</h3>
          <p>Reduce manual testing time by up to 80% with automated workflows</p>
        </div>
        
        <div class="benefit-item">
          <div class="benefit-icon">👥</div>
          <h3>Team Collaboration</h3>
          <p>Enable non-technical team members to create and maintain automation</p>
        </div>
        
        <div class="benefit-item">
          <div class="benefit-icon">🛡️</div>
          <h3>Quality Assurance</h3>
          <p>Ensure consistent testing across different environments and browsers</p>
        </div>
        
        <div class="benefit-item">
          <div class="benefit-icon">📈</div>
          <h3>Scalability</h3>
          <p>Easily scale automation efforts across multiple projects and teams</p>
        </div>
      </div>
    </div>
    
    <div class="content-section">
      <h2 class="section-title">
        <span class="title-icon">🚀</span>
        Getting Started
      </h2>
      <div class="getting-started-steps">
        <div class="start-step">
          <div class="start-icon">1️⃣</div>
          <div class="start-content">
            <h4>Install the Chrome Extension</h4>
            <p>Download and install the XPeer.AI Recorder extension from the Chrome Web Store</p>
          </div>
        </div>
        
        <div class="start-step">
          <div class="start-icon">2️⃣</div>
          <div class="start-content">
            <h4>Access the Web Platform</h4>
            <p>Sign up for an account and access the web application at xpeer.ai</p>
          </div>
        </div>
        
        <div class="start-step">
          <div class="start-icon">3️⃣</div>
          <div class="start-content">
            <h4>Record Your First Flow</h4>
            <p>Start recording your interactions and see them appear in the Canvas</p>
          </div>
        </div>
      </div>
    </div>
    
    <div class="blue-note">
      <span class="icon">💡</span>
      <strong>Ready to get started?</strong> 
      <span>Check out our Installation guide and Best Practices to begin your automation journey with XPeer.AI.</span>
    </div>
  </div>
  
  <style>
  .what-is-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 30px 0;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
    border-radius: 15px;
  }
  
  .page-header h1 {
    font-size: 2.5em;
    margin: 0 0 10px 0;
    font-weight: 700;
    color: white;
  }
  
  .page-subtitle {
    font-size: 1.2em;
    opacity: 0.9;
    margin: 0;
  }
  
  .content-section {
    margin-bottom: 40px;
    background: white;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .section-title {
    display: flex;
    align-items: center;
    font-size: 1.8em;
    color: #333;
    margin-bottom: 25px;
    border-bottom: 3px solid #667eea;
    padding-bottom: 10px;
  }
  
  .title-icon {
    font-size: 1.2em;
    margin-right: 15px;
  }
  
  .lead-text {
    font-size: 1.2em;
    line-height: 1.8;
    color: #555;
    margin-bottom: 30px;
    text-align: center;
    font-style: italic;
  }
  
  .key-features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
  }
  
  .feature-card {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 12px;
    text-align: center;
    border-left: 4px solid #667eea;
    transition: transform 0.2s ease;
  }
  
  .feature-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  
  .feature-icon {
    font-size: 2.5em;
    margin-bottom: 15px;
  }
  
  .feature-card h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.3em;
  }
  
  .feature-card p {
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
  
  .architecture-overview {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }
  
  .component-item {
    display: flex;
    align-items: flex-start;
    gap: 25px;
    padding: 25px;
    background: #f8f9fa;
    border-radius: 12px;
    border-left: 4px solid #28a745;
  }
  
  .component-icon {
    font-size: 3em;
    flex-shrink: 0;
  }
  
  .component-details h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.5em;
  }
  
  .component-details p {
    color: #666;
    margin-bottom: 15px;
    line-height: 1.6;
  }
  
  .component-details ul {
    margin: 0;
    padding-left: 20px;
  }
  
  .component-details li {
    margin-bottom: 8px;
    color: #555;
    line-height: 1.5;
  }
  
  .workflow-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 20px;
  }
  
  .workflow-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 200px;
  }
  
  .step-number {
    background: #667eea;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2em;
    margin-bottom: 15px;
  }
  
  .step-content h4 {
    color: #333;
    margin-bottom: 10px;
    font-size: 1.2em;
  }
  
  .step-content p {
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
  
  .workflow-arrow {
    font-size: 2em;
    color: #667eea;
    font-weight: bold;
    display:none
  }
  
  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
  }
  
  .benefit-item {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 12px;
    text-align: center;
    border-left: 4px solid #28a745;
    transition: transform 0.2s ease;
  }
  
  .benefit-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  
  .benefit-icon {
    font-size: 2.5em;
    margin-bottom: 15px;
  }
  
  .benefit-item h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.3em;
  }
  
  .benefit-item p {
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
  
  .getting-started-steps {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .start-step {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 10px;
    border-left: 4px solid #667eea;
  }
  
  .start-icon {
    font-size: 1.5em;
    flex-shrink: 0;
  }
  
  .start-content h4 {
    color: #333;
    margin-bottom: 10px;
    font-size: 1.3em;
  }
  
  .start-content p {
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
  
  .blue-note {
    background: #e3f2fd;
    border: 1px solid #2196f3;
    border-radius: 8px;
    padding: 20px;
    margin-top: 30px;
    display: flex;
    align-items: flex-start;
    gap: 15px;
  }
  
  .blue-note .icon {
    color: #2196f3;
    font-size: 1.2em;
    margin-top: 2px;
  }
  
  .blue-note strong {
    color: #1976d2;
  }
  
  .blue-note span {
    color: #1565c0;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    .key-features {
      grid-template-columns: 1fr;
    }
    
    .benefits-grid {
      grid-template-columns: 1fr;
    }
    
    .workflow-steps {
      flex-direction: column;
    }
    
    .workflow-arrow {
      transform: rotate(90deg);
    }
    
    .component-item {
      flex-direction: column;
      text-align: center;
    }
  }
  </style>
`
};
export const browserNavMap: { [key: string]: string } = {
  'open-url': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">open-url</h1>
    <div class="main-section-title">Description</div>
    <p>Navigates the browser to a specified web address. This action is typically used as the first step in an automation flow to load a website or application page.</p>
    
    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">open-url</div>
      <div class="press-key-key">URL</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>open-url</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target URL to open, with blue background.</li>
    </ul>
    
    <div class="main-section-title">How It Works</div>
    <p>The system navigates to the specified web address in a new or existing browser window, depending on the automation context.</p>
    
    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Starting a test by opening a website</li>
      <li>Accessing an application portal or login page</li>
    </ul>
                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/open url.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>
    
    <div class="blue-note"><span class="icon">&#9432;</span><strong>Note:</strong> <span>Make sure the URL starts with <code>http://</code> or <code>https://</code> for proper redirection.</span></div>
  </div>
`  ,
  'get-current-url': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-current-url</h1>

    <div class="main-section-title">Description</div>
    <p>Retrieves the current browser URL and stores it into a specified variable.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">get-current-url</div>
      <div class="press-key-key">##variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>get-current-url</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable (blue background) where the current URL will be stored.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system reads the current URL from the active browser window
      and assigns it to the specified variable.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating navigation or redirection</li>
      <li>Storing the URL for later comparison or logging</li>
      <li>Debugging test flows</li>
    </ul>
                                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get current url.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The variable must start with <code>##</code> (for example: <code>##myurl</code>).
        The current page URL will be stored in this variable.
      </span>
    </div>
  </div>
`  ,
  'open-new-tab': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">open-new-tab</h1>

    <div class="main-section-title">Description</div>
    <p>Opens a specified web address in a new browser tab.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">open-new-tab</div>
      <div class="press-key-key">URL</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>open-new-tab</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target URL to open, shown with a blue background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      The system opens the specified URL in a new browser tab while keeping
      the current tab active or available, depending on the automation context.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Opening external links without losing the current page</li>
      <li>Running parallel validations across multiple tabs</li>
      <li>Testing multi-tab workflows</li>
    </ul>
                                                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/open new tab.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the URL starts with <code>http://</code> or <code>https://</code>.
        The tab will be opened separately from the current page.
      </span>
    </div>
  </div>
` ,
  'open-new-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">open-new-window</h1>

    <div class="main-section-title">Description</div>
    <p>Opens a new browser window.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">open-new-window</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>open-new-window</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system launches a new browser window.
      The window starts as a blank page or default browser page,
      depending on the automation environment.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Testing multi-window workflows</li>
      <li>Launching a fresh browser context</li>
      <li>Separating test scenarios across windows</li>
    </ul>
                                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/open new window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any additional parameters.
        Use <code>open-url</code> or <code>open-new-tab</code> to load a page afterward.
      </span>
    </div>
  </div>
`  ,
  'close-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">close-window</h1>

    <div class="main-section-title">Description</div>
    <p>Closes a browser window based on the specified window number.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">close-window</div>
      <div class="press-key-key">number</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>close-window</b>) with maroon background.</li>
      <li><b>Second Part</b> – The window number to close, shown with a blue background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system identifies the browser window using the given number
      and closes that specific window.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Closing a specific window in multi-window workflows</li>
      <li>Handling pop-ups or secondary windows by index</li>
      <li>Managing complex browser sessions</li>
    </ul>
                                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/close window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Window numbering usually starts from <code>0</code> or <code>1</code>
        depending on the automation context.
        Ensure the correct window index is provided.
      </span>
    </div>
  </div>
`  ,
  'close-current-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">close-current-window</h1>

    <div class="main-section-title">Description</div>
    <p>Closes the currently focused browser window.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">close-current-window</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>close-current-window</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      The system closes only the active (current) browser window.
      Other open windows remain unaffected.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Closing pop-ups or child windows</li>
      <li>Cleaning up after multi-window tests</li>
      <li>Returning control to a main window</li>
    </ul>
                                                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/close current window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        If this is the last open window, the browser session may end.
        Ensure another window is available if execution should continue.
      </span>
    </div>
  </div>
`  ,
  'close-all-except-original-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      close-all-except-original-window
    </h1>

    <div class="main-section-title">Description</div>
    <p>Closes all browser windows except the original (first opened) window.</p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">close-all-except-original-window</div>
    </div>
    <ul>
      <li>
        <b>Single Part</b> – The action
        (<b>close-all-except-original-window</b>) with maroon background.
      </li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system identifies the original browser window
      (the first window opened in the session) and closes all other open windows.
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Cleaning up after multi-window operations</li>
      <li>Closing pop-ups and child windows</li>
      <li>Returning focus to the main application window</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The original window remains active after this action.
        Any unsaved data in other windows may be lost.
      </span>
    </div>
  </div>
`  ,
  'switch-to-main-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-main-window
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the focus to the main frame of the current browser window.  
      Exits any nested iframes and returns to the top-level content of the current tab.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-main-window</div>
    </div>
    <ul>
      <li>
        <b>Single Part</b> – The action
        (<b>switch-to-main-window</b>) with maroon background.
      </li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Exits any iframe currently in focus.</li>
        <li>Returns control to the top-level content of the active browser tab.</li>
        <li>Keeps the same window/tab open; only the frame context changes.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Returning to the main frame after interacting with nested iframes</li>
      <li>Resuming actions in the top-level content of a web page</li>
      <li>Stabilizing automation flows that involve multiple frames</li>
    </ul>
                                                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to main window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not switch tabs or windows.  
        To switch to another window, use <code>switch-to-window</code>.  
        Use this whenever you are inside an iframe and want to return to the main page.
      </span>
    </div>
  </div>
`  ,
  'switch-to-original-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-original-window
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to the original (first opened) browser window of the session.
      Useful when multiple windows have been opened and you want to return to the main context.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-original-window</div>
    </div>
    <ul>
      <li>
        <b>Single Part</b> – The action (<b>switch-to-original-window</b>) with maroon background.
      </li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the original window that was first opened in the session.</li>
        <li>Switches the active focus to that window.</li>
        <li>Other open windows remain open but lose focus.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Returning to the main application window after working with pop-ups or child windows</li>
      <li>Resuming execution in the primary browser window</li>
      <li>Stabilizing multi-window automation flows</li>
    </ul>
                                                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to original window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The original window must still be open for this action to succeed.  
        Use <code>close-all-except-original-window</code> to clean up other windows if needed.
      </span>
    </div>
  </div>
`  ,
  'switch-to-new-window': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-new-window
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to the newest browser window that was opened in the session.
      Useful when multiple windows are open and you want to interact with the most recent one.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-new-window</div>
    </div>
    <ul>
      <li>
        <b>Single Part</b> – The action (<b>switch-to-new-window</b>) with maroon background.
      </li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the most recently opened browser window.</li>
        <li>Switches the active focus to that window.</li>
        <li>Other open windows remain open but lose focus.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a pop-up or child window</li>
      <li>Performing actions in a newly opened tab or window</li>
      <li>Managing multi-window automation flows</li>
    </ul>
                                                                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to new window.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The new window must already be open for this action to succeed.  
        Use <code>open-new-window</code> or <code>open-new-tab</code> before switching if needed.
      </span>
    </div>
  </div>
`  ,
  'switch-tab-using-index': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-tab-using-index
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to a specific browser tab based on its index number.
      Useful when multiple tabs are open and you need to control a particular one.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-tab-using-index</div>
      <div class="press-key-key">index</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-tab-using-index</b>) with maroon background.</li>
      <li><b>Second Part</b> – The tab index number (blue background) to switch to.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the tab using the provided index number.</li>
        <li>Switches focus to that tab while keeping other tabs open.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a specific tab for testing or automation</li>
      <li>Performing actions on a particular tab in multi-tab workflows</li>
      <li>Validating navigation or content in multiple open tabs</li>
    </ul>
                                                                                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch tab using index.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Tab indexing usually starts from <code>0</code> for the first tab.  
        Ensure the index corresponds to an open tab to avoid errors.
      </span>
    </div>
  </div>
`  ,
  'switch-window-by-index': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-window-by-index
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to a specific browser window based on its index number.
      Useful when multiple windows are open and you want to interact with a particular one.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-window-by-index</div>
      <div class="press-key-key">window index</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-window-by-index</b>) with maroon background.</li>
      <li><b>Second Part</b> – The window index (blue background) to switch to.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the browser window using the provided index number.</li>
        <li>Switches focus to that window while keeping other windows open.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a specific window in multi-window workflows</li>
      <li>Performing actions on a particular window without affecting others</li>
      <li>Testing scenarios involving multiple browser windows</li>
    </ul>
                                                                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to window by index.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Window indexing usually starts from <code>0</code> for the first window.  
        Ensure the index corresponds to an open window to avoid errors.
      </span>
    </div>
  </div>
`  ,
  'switch-window-by-title': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-window-by-title
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to a specific browser window based on its title.
      Useful when multiple windows are open and you want to interact with a window by name.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-window-by-title</div>
      <div class="press-key-key">window title</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-window-by-title</b>) with maroon background.</li>
      <li><b>Second Part</b> – The window title (blue background) to switch to.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Searches through all open browser windows for a window matching the provided title.</li>
        <li>Switches focus to that window while keeping other windows open.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a specific window when multiple windows have different titles</li>
      <li>Performing actions in a window identified by its name</li>
      <li>Stabilizing automation flows involving multiple browser windows</li>
    </ul>
                                                                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to window by title.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the window title exactly matches the target window's title.  
        Titles are case-sensitive and must correspond to an open window.
      </span>
    </div>
  </div>
`  ,
  'switch-window-by-url': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-window-by-url
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches focus to a specific browser window based on its URL.
      Useful when multiple windows are open and you want to interact with a window by its web address.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-window-by-url</div>
      <div class="press-key-key">window URL</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-window-by-url</b>) with maroon background.</li>
      <li><b>Second Part</b> – The window URL (blue background) to switch to.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Searches through all open browser windows for a window matching the provided URL.</li>
        <li>Switches focus to that window while keeping other windows open.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a specific window when multiple windows have different URLs</li>
      <li>Performing actions in a window identified by its URL</li>
      <li>Automating workflows where windows are dynamically opened by URL</li>
    </ul>
                                                                                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to window by url.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the URL exactly matches the target window's URL.  
        The match may be case-sensitive depending on the system.
      </span>
    </div>
  </div>
`  ,
  'get-current-window-index': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      get-current-window-index
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Retrieves the index number of the currently active browser window
      and stores it in a variable for later use.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">get-current-window-index</div>
      <div class="press-key-key">##myValue</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>get-current-window-index</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable to store the current window index (blue background). Example: <code>##myValue</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the currently active browser window.</li>
        <li>Retrieves its index number (starting from 0 or 1 depending on the system).</li>
        <li>Stores the index in the specified variable for future use.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Storing the current window index before switching to another window or tab</li>
      <li>Returning to the original window later in a multi-window workflow</li>
      <li>Dynamic window management in automation scripts</li>
    </ul>

                                                                                                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get current window index.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Use a variable name starting with <code>##</code> to store the window index.  
        Example: <code>##currentWindowIndex</code>.
      </span>
    </div>
  </div>
`  ,
  'get-window-count': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      get-window-count
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Retrieves the total number of currently open browser windows
      and stores the value in a variable for later use.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">get-window-count</div>
      <div class="press-key-key">##myValue</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>get-window-count</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable to store the total window count (blue background). Example: <code>##myValue</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Counts all currently open browser windows.</li>
        <li>Stores the count in the specified variable for future use.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Checking how many windows are open in a session</li>
      <li>Performing conditional logic based on the number of open windows</li>
      <li>Managing multi-window workflows dynamically</li>
    </ul>
                                                                                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get window count.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Use a variable name starting with <code>##</code> to store the count.  
        Example: <code>##totalWindows</code>.
      </span>
    </div>
  </div>
`  ,
  'refresh': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      refresh
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Refreshes or reloads the currently active browser window or tab.
      Useful to ensure the page content is up-to-date.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">refresh</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>refresh</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Reloads the current page in the active tab or window.</li>
        <li>Keeps the current session, cookies, and page state intact.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Refreshing a page after submitting a form or making changes</li>
      <li>Updating dynamic content during automation flows</li>
      <li>Retrying page load if content did not load correctly</li>
    </ul>
                                                                                                                                                                                                                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/refresh.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not open a new tab or window; it only refreshes the current one.
      </span>
    </div>
  </div>
`  ,
  'switch-to-iframe': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-iframe
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the context to a specified iframe within the current page.
      After switching, all actions will be performed inside this iframe until switched back to the main frame.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-iframe</div>
      <div class="press-key-element">iframe element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-to-iframe</b>) with maroon background.</li>
      <li><b>Second Part</b> – The iframe element to switch into (gray background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Focuses all subsequent actions inside the specified iframe.</li>
        <li>Maintains the main page context in the background until switched back.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Interacting with elements inside an iframe</li>
      <li>Filling forms, clicking buttons, or reading content within an iframe</li>
      <li>Automating pages that embed content in multiple iframes</li>
    </ul>
                                                                                                                                                                                                                                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to iframe.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the correct iframe element is targeted in the gray section.  
        Use <code>switch-to-main-window</code> to exit the iframe and return to the top-level content.
      </span>
    </div>
  </div>
`  ,
  'switch-to-iframe-by-index': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-iframe-by-index
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the context to a specific iframe within the current page using its index number.
      After switching, all actions will be performed inside this iframe until switched back to the main frame.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-iframe-by-index</div>
      <div class="press-key-key">index</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-to-iframe-by-index</b>) with maroon background.</li>
      <li><b>Second Part</b> – The index number of the iframe (blue background), supports only one value.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Identifies the iframe based on the provided index (starting from 0).</li>
        <li>Focuses all subsequent actions inside that iframe.</li>
        <li>Maintains the main page context in the background until switched back.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a specific iframe when multiple iframes exist on a page</li>
      <li>Performing actions inside a targeted iframe by its index</li>
      <li>Automating pages with multiple embedded frames</li>
    </ul>
                                                                                                                                                                                                                                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to iframe by index.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The index usually starts from <code>0</code> for the first iframe.  
        Use <code>switch-to-main-window</code> to exit the iframe and return to the top-level content.
      </span>
    </div>
  </div>
`  ,
  'switch-to-iframe-by': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-iframe-by
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the context to a specific iframe within the current page using one or more locators.
      Locators can include the iframe's <code>name</code>, <code>ID</code>, or <code>index</code>.  
      If multiple locators are provided, the system tries them in the order specified.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-iframe-by</div>
      <div class="press-key-key">locator1, locator2, ...</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>switch-to-iframe-by</b>) with maroon background.</li>
      <li><b>Second Part</b> – Comma-separated locators (blue background). Example: <code>myFrameName, iframeID, 2</code></li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Tries each locator in order until it finds the matching iframe.</li>
        <li>Switches focus to that iframe for subsequent actions.</li>
        <li>If no iframe is found, the action fails.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to an iframe when its exact identifier is uncertain</li>
      <li>Providing multiple fallback locators (name, ID, index)</li>
      <li>Automating pages with dynamic iframe attributes</li>
    </ul>
                                                                                                                                                                                                                                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to iframe by.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Use comma-separated values for locators.  
        Examples: <code>loginFrame, iframeID123, 1</code>.  
        Index values start from <code>0</code>.  
        Use <code>switch-to-main-window</code> to exit the iframe.
      </span>
    </div>
  </div>
`,
  'switch-to-default-content': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-default-content
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Exits the currently active iframe and switches back to the top-level (main) content of the current tab.
      All subsequent actions will be performed in the main page context.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-default-content</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>switch-to-default-content</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Exits the currently active iframe.</li>
        <li>Returns focus to the main (top-level) frame of the page.</li>
        <li>Subsequent actions will now operate on the main page instead of the iframe.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Returning to the main page after interacting with an iframe</li>
      <li>Performing actions outside of any iframe</li>
      <li>Resetting the context for automation flows involving multiple iframes</li>
    </ul>
                                                                                                                                                                                                                                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to default content.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any value.  
        Use <code>switch-to-iframe</code> or <code>switch-to-iframe-by</code> to enter another iframe later.
      </span>
    </div>
  </div>
`,
  'open-secondary-session': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      open-secondary-session
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Opens a new secondary browser session.  
      This allows running a separate automation flow in a different browser session without affecting the primary session.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">open-secondary-session</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>open-secondary-session</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Opens a new, separate browser session independent of the primary session.</li>
        <li>Maintains isolated cookies, cache, and session data.</li>
        <li>Allows automation actions to be performed in parallel or without interfering with the main session.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Logging in with a different user account simultaneously</li>
      <li>Testing workflows that require multiple sessions</li>
      <li>Running parallel automation flows in separate browser instances</li>
    </ul>
                                                                                                                                                                                                                                                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/open secondary session.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any value.  
        Use <code>switch-to-session</code> to switch between sessions if needed.
      </span>
    </div>
  </div>
`,
  'close-secondary-session': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      close-secondary-session
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Closes the currently active secondary browser session.  
      This ends the session and releases all associated resources, including cookies, cache, and temporary data.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">close-secondary-session</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>close-secondary-session</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Closes the active secondary session completely.</li>
        <li>Terminates all open tabs or windows within that session.</li>
        <li>Does not affect the primary session or other secondary sessions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Ending a test session after completing automation tasks</li>
      <li>Releasing system resources used by secondary sessions</li>
      <li>Switching back fully to the primary session</li>
    </ul>
                                                                                                                                                                                                                                                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/close secondary session.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any value.  
        Ensure the secondary session is active before executing this action.
      </span>
    </div>
  </div>
`,
  'switch-to-session-primary': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-session-primary
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the automation context back to the primary browser session.  
      All subsequent actions will be performed in the primary session.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-session-primary</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>switch-to-session-primary</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Focuses all subsequent automation actions on the primary session.</li>
        <li>Does not close or affect any secondary sessions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Returning to the main session after working in a secondary session</li>
      <li>Performing actions that must be executed in the primary session</li>
      <li>Coordinating workflows across primary and secondary sessions</li>
    </ul>
                                                                                                                                                                                                                                                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to session primary.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any value.  
        Ensure the primary session is open before switching.
      </span>
    </div>
  </div>
`,
  'switch-to-session-secondary': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      switch-to-session-secondary
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Switches the automation context to the active secondary browser session.  
      All subsequent actions will be performed in the secondary session.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">switch-to-session-secondary</div>
    </div>
    <ul>
      <li><b>Single Part</b> – The action (<b>switch-to-session-secondary</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Focuses all subsequent automation actions on the secondary session.</li>
        <li>Does not close or affect the primary session.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Switching to a secondary session to perform actions in a different user context</li>
      <li>Running workflows in parallel with the primary session</li>
      <li>Testing multi-session scenarios such as multiple logins</li>
    </ul>
                                                                                                                                                                                                                                                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/switch to session secondary.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action does not require any value.  
        Ensure a secondary session is already open before switching.
      </span>
    </div>
  </div>
`
};
export const userInteractionMap: { [key: string]: string } = {
  'wait-for-element': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">wait-for-element</h1>

    <div class="main-section-title">Description</div>
    <p>
      Waits for a specified element to appear on the page before proceeding.  
      This action is useful in dynamic web pages where elements load asynchronously or after certain events.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">wait-for-element</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>wait-for-element</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to wait for (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Monitors the page for the presence of the specified element.</li>
        <li>Waits until the element is present and visible, up to a default or configured timeout.</li>
        <li>Once the element appears, execution continues to the next action.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Waiting for a login button to appear after page load.</li>
      <li>Ensuring that dynamic content or AJAX elements are present before interacting.</li>
      <li>Preventing errors caused by performing actions on elements that haven’t loaded yet.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>wait-for-element</code>  
        <b>Element:</b> <code>#loginButton</code>
      </li>
      <li>Result: The system waits until the login button appears on the page before executing the next action.</li>

      <li>
        <b>Action:</b> <code>wait-for-element</code>  
        <b>Element:</b> <code>//div[@class='dynamic-content']</code>
      </li>
      <li>Result: Execution pauses until the dynamic content div is present on the page.</li>
    </ul>
                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/wait for element.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct.  
        If the element does not appear within the timeout period, the action may fail.  
        This action does not interact with the element; it only waits for its presence.
      </span>
    </div>
  </div>
`  ,
  'wait-for-element-visibility': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">wait-for-element-visibility</h1>

    <div class="main-section-title">Description</div>
    <p>
      Waits for a specified element to become **visible** on the page before proceeding.  
      This action ensures that the element is not only present in the DOM but also displayed to the user.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">wait-for-element-visibility</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>wait-for-element-visibility</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to wait for visibility (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Monitors the page for the specified element.</li>
        <li>Waits until the element becomes visible (displayed on the page), up to a default or configured timeout.</li>
        <li>Once the element is visible, execution continues to the next action.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Waiting for a button or input field to be visible before clicking or typing.</li>
      <li>Ensuring pop-ups, modals, or dynamic content are displayed before interacting.</li>
      <li>Preventing errors caused by interacting with elements that exist but are hidden.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>wait-for-element-visibility</code>  
        <b>Element:</b> <code>#submitButton</code>
      </li>
      <li>Result: Execution pauses until the submit button is visible on the page before proceeding.</li>

      <li>
        <b>Action:</b> <code>wait-for-element-visibility</code>  
        <b>Element:</b> <code>//div[@class='popup-message']</code>
      </li>
      <li>Result: Execution waits until the popup message becomes visible on the page.</li>
    </ul>
                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/wait for element visibility.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct.  
        If the element does not become visible within the timeout period, the action may fail.  
        This action only waits for visibility; it does not perform any clicks or interactions.
      </span>
    </div>
  </div>
`  ,
  'wait-for-element-clickability': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">wait-for-element-clickability</h1>

    <div class="main-section-title">Description</div>
    <p>
      Waits for a specified element to become **clickable** on the page before proceeding.  
      This ensures the element is both visible and enabled, preventing errors when attempting to click it.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">wait-for-element-clickability</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>wait-for-element-clickability</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to wait for clickability (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Monitors the page for the specified element.</li>
        <li>Waits until the element is visible and enabled for clicking, up to a default or configured timeout.</li>
        <li>Once the element is clickable, execution continues to the next action.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Waiting for a submit button to be enabled before clicking.</li>
      <li>Ensuring dynamic or AJAX-loaded buttons are ready for interaction.</li>
      <li>Preventing errors caused by attempting to click hidden or disabled elements.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>wait-for-element-clickability</code>  
        <b>Element:</b> <code>#submitButton</code>
      </li>
      <li>Result: Execution pauses until the submit button is clickable before proceeding.</li>

      <li>
        <b>Action:</b> <code>wait-for-element-clickability</code>  
        <b>Element:</b> <code>//button[@class='dynamic-action']</code>
      </li>
      <li>Result: Execution waits until the dynamic-action button is visible and enabled for clicking.</li>
    </ul>
                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/wait for element clickability.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct.  
        If the element does not become clickable within the timeout period, the action may fail.  
        This action only waits for clickability; it does not perform the click itself.
      </span>
    </div>
  </div>
`  ,
  'pause': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">pause</h1>

    <div class="main-section-title">Description</div>
    <p>
      Pauses the execution of the automation for a specified duration.  
      This is useful when you need to wait for certain actions or events without checking for element conditions.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">pause</div>
      <div class="press-key-key">Duration (ms)</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>pause</b>) with maroon background.</li>
      <li><b>Second Part</b> – The duration in milliseconds (blue background). For example, <code>2000</code> means 2 seconds.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Pauses execution for the specified duration in milliseconds.</li>
        <li>After the pause, the next action in the automation flow is executed.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Waiting a fixed time for animations or transitions to complete.</li>
      <li>Delaying execution between actions for testing purposes.</li>
      <li>Introducing short delays in loops or sequences where dynamic waits are not required.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>pause</code>  
        <b>Duration:</b> <code>2000</code>
      </li>
      <li>Result: Execution pauses for 2 seconds before continuing to the next action.</li>

      <li>
        <b>Action:</b> <code>pause</code>  
        <b>Duration:</b> <code>500</code>
      </li>
      <li>Result: Execution pauses for 0.5 seconds before proceeding.</li>
    </ul>
                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/pause.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The duration is specified in milliseconds.  
        Use this action sparingly; dynamic waits like <code>wait-for-element</code> are often preferred for more reliable automation.
      </span>
    </div>
  </div>
`  ,
  'click': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">click</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a click action on a specified element.  
      This is one of the most common actions used to interact with buttons, links, checkboxes, or any clickable element on a page.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">click</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>click</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to click (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Simulates a mouse click on the element.</li>
        <li>Triggers any associated click events, such as navigation, form submission, or JavaScript actions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Clicking buttons, links, checkboxes, or radio buttons.</li>
      <li>Submitting forms or triggering modal popups.</li>
      <li>Interacting with menus, tabs, or dynamic page elements.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>click</code>  
        <b>Element:</b> <code>#loginButton</code>
      </li>
      <li>Result: The login button is clicked, triggering its click event.</li>

      <li>
        <b>Action:</b> <code>click</code>  
        <b>Element:</b> <code>//a[text()='Next Page']</code>
      </li>
      <li>Result: The "Next Page" link is clicked, navigating to the next page.</li>
    </ul>
                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/click.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible and enabled.  
        Clicking hidden or disabled elements may cause the action to fail.
      </span>
    </div>
  </div>
`  ,
  'move-and-click': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">move-and-click</h1>

    <div class="main-section-title">Description</div>
    <p>
      Moves the mouse cursor to a specified element and performs a click.  
      Useful for elements that require hovering before clicking or when direct click may fail.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">move-and-click</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>move-and-click</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to move to and click (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Moves the mouse cursor to the specified element.</li>
        <li>Waits for the element to become visible and enabled.</li>
        <li>Performs a click on the element, triggering any associated click events.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Clicking elements that require hovering, like dropdown menus or tooltips.</li>
      <li>Interacting with elements that are dynamically displayed on hover.</li>
      <li>Ensuring reliable clicks on elements where direct click may fail.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>move-and-click</code>  
        <b>Element:</b> <code>#menuButton</code>
      </li>
      <li>Result: The cursor moves to the menu button and clicks it, opening the menu.</li>

      <li>
        <b>Action:</b> <code>move-and-click</code>  
        <b>Element:</b> <code>//button[text()='Submit']</code>
      </li>
      <li>Result: The cursor moves to the submit button and clicks it, submitting the form.</li>
    </ul>
                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/move and click.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct.  
        The element must be visible and enabled for the click to succeed.  
        This action combines mouse movement and clicking in a single step.
      </span>
    </div>
  </div>
`  ,
  'double-click': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">double-click</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a **double click** on a specified element.  
      This action is useful for triggering events that specifically require a double click, such as opening files, selecting text, or special UI actions.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">double-click</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>double-click</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to double-click (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Performs two consecutive clicks on the element in rapid succession.</li>
        <li>Triggers any double-click-specific events associated with the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Opening files or folders in applications that require double click.</li>
      <li>Selecting text or activating text boxes that respond to double-click.</li>
      <li>Triggering special UI elements or scripts that require double click.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>double-click</code>  
        <b>Element:</b> <code>#fileIcon</code>
      </li>
      <li>Result: The file icon is double-clicked, opening the file.</li>

      <li>
        <b>Action:</b> <code>double-click</code>  
        <b>Element:</b> <code>//div[@class='editable-text']</code>
      </li>
      <li>Result: The editable text area is double-clicked, enabling text editing.</li>
    </ul>
                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/double click.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        Double click may fail if the element is hidden, disabled, or not fully loaded.
      </span>
    </div>
  </div>
`  ,
  'right-click': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">right-click</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a **right-click** (context click) on a specified element.  
      Useful for opening context menus or triggering actions that are associated with right-click events.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">right-click</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>right-click</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to right-click (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Simulates a right-click (context click) on the element.</li>
        <li>Triggers any context menu or right-click-specific events associated with the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Opening context menus on elements.</li>
      <li>Triggering custom right-click actions in applications or web pages.</li>
      <li>Interacting with elements that respond differently to right-click than left-click.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>right-click</code>  
        <b>Element:</b> <code>#fileIcon</code>
      </li>
      <li>Result: The file icon is right-clicked, opening the context menu.</li>

      <li>
        <b>Action:</b> <code>right-click</code>  
        <b>Element:</b> <code>//div[@class='custom-area']</code>
      </li>
      <li>Result: The custom area is right-clicked, triggering its context menu or right-click event.</li>
    </ul>
                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/right click.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        Right-click may fail if the element is hidden, disabled, or not fully loaded.
      </span>
    </div>
  </div>
`  ,
  'mouse-over': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">mouse-over</h1>

    <div class="main-section-title">Description</div>
    <p>
      Moves the mouse cursor over a specified element without clicking.  
      Useful for triggering hover effects, dropdowns, tooltips, or other UI changes that occur on mouse hover.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">mouse-over</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>mouse-over</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to hover over (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Moves the mouse cursor over the element without clicking.</li>
        <li>Triggers any hover or mouse-over effects associated with the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Opening dropdown menus on hover.</li>
      <li>Displaying tooltips or additional UI information.</li>
      <li>Triggering animations or hover effects before clicking.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>mouse-over</code>  
        <b>Element:</b> <code>#menuButton</code>
      </li>
      <li>Result: The mouse cursor moves over the menu button, showing the dropdown menu.</li>

      <li>
        <b>Action:</b> <code>mouse-over</code>  
        <b>Element:</b> <code>//div[@class='tooltip-area']</code>
      </li>
      <li>Result: The mouse cursor hovers over the tooltip area, displaying the tooltip.</li>
    </ul>
                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/mouse over.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        Hover effects may not trigger if the element is hidden or covered by another element.
      </span>
    </div>
  </div>
`  ,
  'move-to-element': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">move-to-element</h1>

    <div class="main-section-title">Description</div>
    <p>
      Moves the mouse pointer to a specified element on the page.  
      This action is commonly used to hover over elements to reveal hidden menus, tooltips, or trigger mouse-over events.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">move-to-element</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>move-to-element</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element (blue background). Can be identified using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the target element on the page.</li>
        <li>Moves the mouse pointer to the center of that element.</li>
        <li>Triggers any hover effects, tooltips, or menus associated with mouse-over events.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Hovering over a menu to reveal a dropdown list.</li>
      <li>Triggering tooltips or pop-up information when hovering over an element.</li>
      <li>Preparing the mouse position before clicking hidden or hover-based elements.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>move-to-element</code>  
        <b>Element:</b> <code>#menuButton</code>
      </li>
      <li>Result: Mouse moves to the element with ID <code>menuButton</code> and any hover events are triggered.</li>

      <li>
        <b>Action:</b> <code>move-to-element</code>  
        <b>Element:</b> <code>//div[@class='tooltip-target']</code>
      </li>
      <li>Result: Mouse moves to the element located by XPath, displaying the tooltip.</li>
    </ul>
                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/move to element.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element exists and is visible on the page before using this action.  
        This action only moves the mouse pointer; to perform a click after moving, use a separate <code>click-element</code> action.
      </span>
    </div>
  </div>
`  ,
  'drag-and-drop': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">drag-and-drop</h1>

    <div class="main-section-title">Description</div>
    <p>
      Drags a source element and drops it onto a target element.  
      This action is commonly used for reordering items, moving elements in drag-and-drop interfaces, or performing drag actions in web applications.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">drag-and-drop</div>
      <div class="press-key-element">Source Element</div>
      <div class="press-key-element">Target Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>drag-and-drop</b>) with maroon background.</li>
      <li><b>Second Part</b> – The source element to drag (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The target element to drop into (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the source element on the page.</li>
        <li>Finds the target element on the page.</li>
        <li>Drags the source element and drops it onto the center of the target element.</li>
        <li>Triggers any drag-and-drop events associated with the elements.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Reordering items in a list or grid using drag-and-drop.</li>
      <li>Moving elements between containers in a web application.</li>
      <li>Testing drag-and-drop functionality in web pages.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>drag-and-drop</code>  
        <b>Source Element:</b> <code>#item1</code>  
        <b>Target Element:</b> <code>#container2</code>
      </li>
      <li>Result: The element with ID <code>item1</code> is dragged and dropped into the element with ID <code>container2</code>.</li>

      <li>
        <b>Action:</b> <code>drag-and-drop</code>  
        <b>Source Element:</b> <code>//div[@class='draggable']</code>  
        <b>Target Element:</b> <code>//div[@class='dropzone']</code>
      </li>
      <li>Result: The source element located by XPath is dragged into the target dropzone element.</li>
    </ul>
                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/drag and drop.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure both source and target elements are visible and interactable before performing this action.  
        Drag-and-drop may trigger additional events depending on the page implementation, so use carefully in testing scenarios.
      </span>
    </div>
  </div>
`  ,
  'drag-and-drop-to-offset': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">drag-and-drop-to-offset</h1>

    <div class="main-section-title">Description</div>
    <p>
      Drags a source element and moves it by a specified offset (x, y) in pixels.  
      This action is useful when you want to drag an element to a position relative to its current location instead of a specific target element.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">drag-and-drop-to-offset</div>
      <div class="press-key-element">Source Element</div>
      <div class="press-key-key">Offset (x,y)</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>drag-and-drop-to-offset</b>) with maroon background.</li>
      <li><b>Second Part</b> – The source element to drag (blue background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The offset value (blue background). Format: <code>x,y</code> in pixels (e.g., <code>0,10</code>, <code>20,30</code>).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the source element on the page.</li>
        <li>Calculates the new position using the specified offset relative to the element’s current position.</li>
        <li>Drags the element to the new coordinates.</li>
        <li>Triggers any drag-and-drop or mouse-move events associated with the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Moving sliders, handles, or draggable items by a precise offset.</li>
      <li>Testing drag functionality where the target is dynamic or not a fixed element.</li>
      <li>Positioning elements within a canvas or grid interface.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>drag-and-drop-to-offset</code>  
        <b>Source Element:</b> <code>#sliderHandle</code>  
        <b>Offset:</b> <code>0,50</code>
      </li>
      <li>Result: The slider handle moves 50 pixels down from its current position.</li>

      <li>
        <b>Action:</b> <code>drag-and-drop-to-offset</code>  
        <b>Source Element:</b> <code>//div[@class='draggable']</code>  
        <b>Offset:</b> <code>20,-10</code>
      </li>
      <li>Result: The draggable element moves 20 pixels right and 10 pixels up from its current position.</li>
    </ul>
                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/drag and drop to offset.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the source element is visible and interactable.  
        The offset is relative to the element’s current position, not absolute page coordinates.  
        Dragging outside visible boundaries may not work depending on the browser or page implementation.
      </span>
    </div>
  </div>
`,
  'drag-and-drop-to-coordinates': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">drag-and-drop-to-coordinates</h1>

    <div class="main-section-title">Description</div>
    <p>
      Drags a specified element and drops it to a specific coordinate (X, Y) relative to its current position or page.  
      Useful for moving elements to precise positions in applications or web pages.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">drag-and-drop-to-coordinates</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">Coordinates (X,Y)</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>drag-and-drop-to-coordinates</b>) with maroon background.</li>
      <li><b>Second Part</b> – The element to drag (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The target coordinates (blue background) in the format <code>X,Y</code>, e.g., <code>0,100</code> to move 0px horizontally and 100px vertically.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Clicks and holds the element.</li>
        <li>Moves the element to the specified coordinates.</li>
        <li>Drops the element at the new position.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Moving draggable elements to exact positions.</li>
      <li>Reordering items in lists or grids.</li>
      <li>Testing drag-and-drop functionality in web applications.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>drag-and-drop-to-coordinates</code>  
        <b>Element:</b> <code>#draggableBox</code>  
        <b>Coordinates:</b> <code>0,100</code>
      </li>
      <li>Result: The draggable box moves vertically down by 100 pixels from its current position.</li>

      <li>
        <b>Action:</b> <code>drag-and-drop-to-coordinates</code>  
        <b>Element:</b> <code>//div[@class='drag-item']</code>  
        <b>Coordinates:</b> <code>50,20</code>
      </li>
      <li>Result: The drag item moves 50px horizontally and 20px vertically from its original position.</li>
    </ul>
                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/drag and drop with coordinates.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        Coordinates are relative to the element's current position or the page depending on the implementation.
      </span>
    </div>
  </div>
`,
  'enter-text': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">enter-text</h1>

    <div class="main-section-title">Description</div>
    <p>
      Enters the specified text into a target input field or textarea element.  
      Useful for filling forms, search boxes, login fields, or any text input on a web page.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">enter-text</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>enter-text</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to enter text into (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The text to enter (blue background). Can be plain text or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Clears any existing text in the field.</li>
        <li>Enters the provided text into the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Filling login forms with username and password.</li>
      <li>Entering search queries in search boxes.</li>
      <li>Populating text fields in web forms or applications.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>enter-text</code>  
        <b>Element:</b> <code>#username</code>  
        <b>Text:</b> <code>JohnDoe</code>
      </li>
      <li>Result: The username field is filled with "JohnDoe".</li>

      <li>
        <b>Action:</b> <code>enter-text</code>  
        <b>Element:</b> <code>//input[@name='email']</code>  
        <b>Text:</b> <code>example@email.com</code>
      </li>
      <li>Result: The email input field is populated with "example@email.com".</li>
    </ul>
                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/enter text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible and enabled.  
        Text can also be passed from a variable using the format <code>##variableName</code>.
      </span>
    </div>
  </div>
`  ,
  'move-click-and-enter-text': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">move-click-and-enter-text</h1>

    <div class="main-section-title">Description</div>
    <p>
      Moves the mouse to a specified element, clicks it, and then enters the provided text.  
      Useful for interacting with input fields that require focus via click before typing.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">move-click-and-enter-text</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>move-click-and-enter-text</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to click and enter text into (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The text to enter (blue background). Can be plain text or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Moves the mouse to the element.</li>
        <li>Clicks the element to focus it.</li>
        <li>Enters the provided text into the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Filling input fields that require focus via click.</li>
      <li>Interacting with web forms or UI elements that don’t accept typing until clicked.</li>
      <li>Automating login forms, search boxes, or editable text areas.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>move-click-and-enter-text</code>  
        <b>Element:</b> <code>#username</code>  
        <b>Text:</b> <code>JohnDoe</code>
      </li>
      <li>Result: The mouse moves to the username field, clicks it, and enters "JohnDoe".</li>

      <li>
        <b>Action:</b> <code>move-click-and-enter-text</code>  
        <b>Element:</b> <code>//input[@name='email']</code>  
        <b>Text:</b> <code>example@email.com</code>
      </li>
      <li>Result: The mouse moves to the email field, clicks it, and enters "example@email.com".</li>
    </ul>
                                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/move click and enter text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible and enabled.  
        Text can also be passed from a variable using the format <code>##variableName</code>.
      </span>
    </div>
  </div>
`  ,
  'move-click-clear-and-enter-text': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">move-click-clear-and-enter-text</h1>

    <div class="main-section-title">Description</div>
    <p>
      Moves the mouse to a specified element, clicks it, clears any existing text, and then enters the provided text.  
      Useful for ensuring input fields are emptied before entering new values.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">move-click-clear-and-enter-text</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>move-click-clear-and-enter-text</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to click, clear, and enter text into (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The text to enter (blue background). Can be plain text or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Moves the mouse to the element.</li>
        <li>Clicks the element to focus it.</li>
        <li>Clears any existing text in the element.</li>
        <li>Enters the provided text into the element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Updating input fields with new values.</li>
      <li>Filling forms where old data must be cleared first.</li>
      <li>Automating login, registration, or search fields that require clearing before typing.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>move-click-clear-and-enter-text</code>  
        <b>Element:</b> <code>#username</code>  
        <b>Text:</b> <code>JohnDoe</code>
      </li>
      <li>Result: The mouse moves to the username field, clicks it, clears existing text, and enters "JohnDoe".</li>

      <li>
        <b>Action:</b> <code>move-click-clear-and-enter-text</code>  
        <b>Element:</b> <code>//input[@name='email']</code>  
        <b>Text:</b> <code>example@email.com</code>
      </li>
      <li>Result: The email field is clicked, cleared, and populated with "example@email.com".</li>
    </ul>
                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/move click clear and enter text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible and enabled.  
        Text can also be passed from a variable using the format <code>##variableName</code>.
      </span>
    </div>
  </div>
`  ,
  'press-key': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">press-key</h1>

    <div class="main-section-title">Description</div>
    <p>
      Simulates pressing a specific keyboard key on a target element.  
      Useful for navigating fields, closing dialogs, selecting options, or triggering keyboard shortcuts.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">press-key</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">Key</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>press-key</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element to send the key press to (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The key to press (blue background). Use text like <code>TAB</code>, <code>ESC</code>, <code>ENTER</code>, <code>ARROW_DOWN</code>, etc.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Focuses the element if needed.</li>
        <li>Simulates pressing the specified keyboard key on that element.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Pressing <code>TAB</code> to move focus to the next input field.</li>
      <li>Pressing <code>ESC</code> to close popups or dialogs.</li>
      <li>Using arrow keys to navigate dropdowns or lists.</li>
      <li>Submitting a form by pressing <code>ENTER</code>.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>press-key</code>  
        <b>Element:</b> <code>#searchBox</code>  
        <b>Key:</b> <code>TAB</code>
      </li>
      <li>Result: Moves focus from the search box to the next input field.</li>

      <li>
        <b>Action:</b> <code>press-key</code>  
        <b>Element:</b> <code>#popupDialog</code>  
        <b>Key:</b> <code>ESC</code>
      </li>
      <li>Result: Closes the popup dialog.</li>

      <li>
        <b>Action:</b> <code>press-key</code>  
        <b>Element:</b> <code>#dropdownMenu</code>  
        <b>Key:</b> <code>ARROW_DOWN</code>
      </li>
      <li>Result: Navigates down the dropdown options.</li>
    </ul>
                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/press key.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        Supported keys include <code>TAB</code>, <code>ENTER</code>, <code>ESC</code>, <code>ARROW_UP</code>, <code>ARROW_DOWN</code>, and others as required.
      </span>
    </div>
  </div>
`  ,
  'submit-form': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">submit-form</h1>

    <div class="main-section-title">Description</div>
    <p>
      Submits a form on the web page by targeting the form element.  
      Useful for triggering form submission without clicking a submit button manually.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">submit-form</div>
      <div class="press-key-element">Form Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>submit-form</b>) with maroon background.</li>
      <li><b>Second Part</b> – The form element to submit (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified form element on the page.</li>
        <li>Triggers the form submission event.</li>
        <li>Processes the form as if the user clicked a submit button.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Submitting login, registration, or search forms programmatically.</li>
      <li>Automating form submissions in workflows or tests.</li>
      <li>Works without manually locating and clicking the submit button.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>submit-form</code>  
        <b>Element:</b> <code>#loginForm</code>
      </li>
      <li>Result: The login form with ID <code>loginForm</code> is submitted.</li>

      <li>
        <b>Action:</b> <code>submit-form</code>  
        <b>Element:</b> <code>//form[@name='signup']</code>
      </li>
      <li>Result: The signup form is submitted.</li>
    </ul>
                                                                                                                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/submit form.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the form element is correctly located and visible.  
        This action triggers the form submission event without needing a submit button click.
      </span>
    </div>
  </div>
`  ,
  'copy': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">copy</h1>

    <div class="main-section-title">Description</div>
    <p>
      Copies the value from one variable or source to another variable.  
      Useful for storing data dynamically during automation and using it later in the workflow.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">copy</div>
      <div class="press-key-key">Source Value</div>
      <div class="press-key-key">Destination Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>copy</b>) with maroon background.</li>
      <li><b>Second Part</b> – The value or variable to copy from (blue background). Can be a text, number, or variable.</li>
      <li><b>Third Part</b> – The destination variable where the value will be stored (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Retrieves the value from the source (text or variable).</li>
        <li>Assigns this value to the destination variable.</li>
        <li>The destination variable can then be used in subsequent actions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Storing user input or field values for later use.</li>
      <li>Copying dynamic data between variables in automation flows.</li>
      <li>Preserving values from web elements or previous actions.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>copy</code>  
        <b>Source Value:</b> <code>##username</code>  
        <b>Destination Variable:</b> <code>##userCopy</code>
      </li>
      <li>Result: The value of variable <code>##username</code> is copied to <code>##userCopy</code>.</li>

      <li>
        <b>Action:</b> <code>copy</code>  
        <b>Source Value:</b> <code>12345</code>  
        <b>Destination Variable:</b> <code>##orderID</code>
      </li>
      <li>Result: The value <code>12345</code> is stored in the variable <code>##orderID</code>.</li>
    </ul>
                                                                                                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/copy.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The source can be plain text, numbers, or variables.  
        Make sure the destination variable is properly defined and follows your system’s variable naming convention.
      </span>
    </div>
  </div>
`  ,
  'upload-file': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">upload-file</h1>

    <div class="main-section-title">Description</div>
    <p>
      Uploads a file to a file input element on the web page.  
      The file must have a publicly accessible URL. Useful for automating file uploads in forms or applications.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">upload-file</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">File URL</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>upload-file</b>) with maroon background.</li>
      <li><b>Second Part</b> – The file input element where the file will be uploaded (gray background). Can be located using ID, CSS selector, XPath, etc.</li>
      <li><b>Third Part</b> – The publicly accessible URL of the file to upload (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified file input element on the page.</li>
        <li>Downloads the file from the provided URL (must be public).</li>
        <li>Uploads the file to the file input element for submission or further actions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Uploading profile pictures, documents, or attachments in forms.</li>
      <li>Automating bulk file uploads using a URL source.</li>
      <li>Testing file upload functionalities in web applications.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>upload-file</code>  
        <b>Element:</b> <code>#profilePicUpload</code>  
        <b>File URL:</b> <code>https://example.com/files/avatar.png</code>
      </li>
      <li>Result: The file at <code>https://example.com/files/avatar.png</code> is uploaded to the input with ID <code>profilePicUpload</code>.</li>

      <li>
        <b>Action:</b> <code>upload-file</code>  
        <b>Element:</b> <code>//input[@name='document']</code>  
        <b>File URL:</b> <code>https://example.com/files/resume.pdf</code>
      </li>
      <li>Result: The PDF file is uploaded to the input element named <code>document</code>.</li>
    </ul>
                                                                                                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/upload file.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The file URL must be publicly accessible; private URLs or local files may not work.  
        Ensure the element is a proper file input (<code>&lt;input type="file"&gt;</code>) and visible on the page.
      </span>
    </div>
  </div>
`  ,
  'scroll-to-element': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">scroll-to-element</h1>

    <div class="main-section-title">Description</div>
    <p>
      Scrolls the page to bring a specific element into view.  
      Useful for interacting with elements that are not visible on the screen without manual scrolling.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">scroll-to-element</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>scroll-to-element</b>) with maroon background.</li>
      <li><b>Second Part</b> – The element to scroll into view (gray background). Can be located using ID, CSS selector, XPath, or other locators.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Scrolls the viewport until the element is visible.</li>
        <li>Leaves the element ready for interaction (click, input, etc.).</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Interacting with elements located far down a page.</li>
      <li>Ensuring buttons, links, or inputs are visible before performing actions.</li>
      <li>Automating workflows on long or scrollable pages.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>scroll-to-element</code>  
        <b>Element:</b> <code>#submitButton</code>
      </li>
      <li>Result: Scrolls the page so that the element with ID <code>submitButton</code> is visible.</li>

      <li>
        <b>Action:</b> <code>scroll-to-element</code>  
        <b>Element:</b> <code>//div[@class='footer-links']</code>
      </li>
      <li>Result: Scrolls to the footer links section on the page.</li>
    </ul>
                                                                                                                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/scroll to element.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Make sure the element exists and is present in the DOM before using this action.  
        This action does not perform any clicks or inputs—it only scrolls to make the element visible.
      </span>
    </div>
  </div>
`
};
export const mathVariableMap: { [key: string]: string } = {
  'add': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">add</h1>

    <div class="main-section-title">Description</div>
    <p>
      Adds a numeric value to a variable.  
      The first value is a variable containing a number, and the second value is the number to add.  
      After execution, the variable is updated with the new sum.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">add</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Number to Add</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>add</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the initial numeric value (blue background). Example: <code>##myValue</code>.</li>
      <li><b>Third Part</b> – The number to add to the variable (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Retrieves the current value of the variable.</li>
        <li>Adds the specified number to it.</li>
        <li>Updates the variable with the new value.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Incrementing counters or numeric values in automation flows.</li>
      <li>Performing calculations dynamically using variables.</li>
      <li>Tracking totals, scores, or quantities during a workflow.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>add</code>  
        <b>Variable:</b> <code>##myValue</code> (contains <code>4</code>)  
        <b>Number to Add:</b> <code>3</code>
      </li>
      <li>Result: <code>##myValue</code> now contains <code>7</code> (4 + 3).</li>

      <li>
        <b>Action:</b> <code>add</code>  
        <b>Variable:</b> <code>##counter</code> (contains <code>10</code>)  
        <b>Number to Add:</b> <code>5</code>
      </li>
      <li>Result: <code>##counter</code> now contains <code>15</code> (10 + 5).</li>
    </ul>
                                                                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/add.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Both the variable and the value to add must be numeric.  
        If the variable contains a non-numeric value, the addition may fail or produce unexpected results.
      </span>
    </div>
  </div>
`,
  'subtract': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">subtract</h1>

    <div class="main-section-title">Description</div>
    <p>
      Subtracts a numeric value from a variable.  
      The first value is a variable containing a number, and the second value is the number to subtract.  
      After execution, the variable is updated with the new value.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">subtract</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Number to Subtract</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>subtract</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the initial numeric value (blue background). Example: <code>##myValue</code>.</li>
      <li><b>Third Part</b> – The number to subtract from the variable (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Retrieves the current value of the variable.</li>
        <li>Subtracts the specified number from it.</li>
        <li>Updates the variable with the new value.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Decrementing counters or numeric values in automation flows.</li>
      <li>Performing calculations dynamically using variables.</li>
      <li>Tracking remaining quantities, scores, or balances during a workflow.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>subtract</code>  
        <b>Variable:</b> <code>##myValue</code> (contains <code>10</code>)  
        <b>Number to Subtract:</b> <code>4</code>
      </li>
      <li>Result: <code>##myValue</code> now contains <code>6</code> (10 - 4).</li>

      <li>
        <b>Action:</b> <code>subtract</code>  
        <b>Variable:</b> <code>##counter</code> (contains <code>15</code>)  
        <b>Number to Subtract:</b> <code>5</code>
      </li>
      <li>Result: <code>##counter</code> now contains <code>10</code> (15 - 5).</li>
    </ul>
                                                                    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/subtract.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Both the variable and the value to subtract must be numeric.  
        If the variable contains a non-numeric value, the subtraction may fail or produce unexpected results.
      </span>
    </div>
  </div>
`  ,
  'multiplication': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">multiplication</h1>

    <div class="main-section-title">Description</div>
    <p>
      Multiplies a numeric variable by a specified number.  
      The first value is a variable containing a number, and the second value is the number to multiply with.  
      After execution, the variable is updated with the product.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">multiplication</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Number to Multiply</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>multiplication</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the initial numeric value (blue background). Example: <code>##myValue</code>.</li>
      <li><b>Third Part</b> – The number to multiply with the variable (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Retrieves the current value of the variable.</li>
        <li>Multiplies it by the specified number.</li>
        <li>Updates the variable with the new product.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Calculating totals, amounts, or scores dynamically.</li>
      <li>Scaling numeric values in automation flows.</li>
      <li>Performing mathematical operations using variables.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>multiplication</code>  
        <b>Variable:</b> <code>##myValue</code> (contains <code>4</code>)  
        <b>Number to Multiply:</b> <code>3</code>
      </li>
      <li>Result: <code>##myValue</code> now contains <code>12</code> (4 × 3).</li>

      <li>
        <b>Action:</b> <code>multiplication</code>  
        <b>Variable:</b> <code>##counter</code> (contains <code>5</code>)  
        <b>Number to Multiply:</b> <code>2</code>
      </li>
      <li>Result: <code>##counter</code> now contains <code>10</code> (5 × 2).</li>
    </ul>
                                                                        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/multiplication.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Both the variable and the multiplier must be numeric.  
        If the variable contains a non-numeric value, the multiplication may fail or produce unexpected results.
      </span>
    </div>
  </div>
`  ,
  'division': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">division</h1>

    <div class="main-section-title">Description</div>
    <p>
      Divides a numeric variable by a specified number.  
      The first value is a variable containing a number, and the second value is the number to divide by.  
      After execution, the variable is updated with the quotient.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">division</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Number to Divide By</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>division</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the initial numeric value (blue background). Example: <code>##myValue</code>.</li>
      <li><b>Third Part</b> – The number to divide the variable by (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Retrieves the current value of the variable.</li>
        <li>Divides it by the specified number.</li>
        <li>Updates the variable with the resulting quotient.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Calculating averages, ratios, or scaled values dynamically.</li>
      <li>Performing mathematical operations using variables in automation flows.</li>
      <li>Reducing numeric values step by step based on input or conditions.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>division</code>  
        <b>Variable:</b> <code>##myValue</code> (contains <code>12</code>)  
        <b>Number to Divide By:</b> <code>3</code>
      </li>
      <li>Result: <code>##myValue</code> now contains <code>4</code> (12 ÷ 3).</li>

      <li>
        <b>Action:</b> <code>division</code>  
        <b>Variable:</b> <code>##counter</code> (contains <code>20</code>)  
        <b>Number to Divide By:</b> <code>4</code>
      </li>
      <li>Result: <code>##counter</code> now contains <code>5</code> (20 ÷ 4).</li>
    </ul>
                                                                            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/multiplication.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Both the variable and the divisor must be numeric.  
        The divisor must not be zero; dividing by zero will result in an error.
      </span>
    </div>
  </div>
`, 'array-size': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">array-size</h1>

    <div class="main-section-title">Description</div>
    <p>
      Retrieves the size (number of elements) of an array from a JSON variable and stores it into another variable.  
      Useful when working with API responses or JSON data to determine the number of items in an array.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">array-size</div>
      <div class="press-key-key">JSON Variable</div>
      <div class="press-key-key">Size Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>array-size</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the JSON array (gray background). For example: <code>##apiResponse</code>.</li>
      <li><b>Third Part</b> – The variable where the array size will be stored (blue background). Must start with <code>##</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Reads the JSON array from the specified variable.</li>
        <li>Counts the number of elements in the array.</li>
        <li>Stores the count into the specified size variable.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Determine the number of items in an API response array.</li>
      <li>Use the count in loops, validations, or conditions.</li>
      <li>Debug or log the number of elements in a dataset.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>array-size</code>  
        <b>JSON Variable:</b> <code>##apiResponse</code>  
        <b>Size Variable:</b> <code>##itemsCount</code>
      </li>
      <li>Result: The variable <code>##itemsCount</code> now contains the number of elements in the JSON array <code>##apiResponse</code>.</li>

      <li>
        <b>Action:</b> <code>array-size</code>  
        <b>JSON Variable:</b> <code>##responseData</code>  
        <b>Size Variable:</b> <code>##userCount</code>
      </li>
      <li>Result: The variable <code>##userCount</code> now contains the number of elements in <code>##responseData</code>.</li>
    </ul>
                                                                                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/array size.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the JSON variable exists and contains an array.  
        The size is stored in the specified variable for further actions or validations.
      </span>
    </div>
  </div>
`, 'random-value-generator': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">random value generator</h1>

  <div class="main-section-title">Description</div>
  <p>
    Generates a random value based on the specified type, size or range, and optional case format.  
    The generated result is stored into the provided variable.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">random value generator</div>
    <div class="press-key-key">Variable</div>
    <div class="press-key-key">TYPE;SIZE_OR_RANGE;CASE</div>
  </div>

  <ul>
    <li><b>First Part</b> – Action name (<b>random value generator</b>) with maroon background.</li>
    <li><b>Second Part</b> – Variable where value will be stored (blue). Must start with <code>##</code>.</li>
    <li><b>Third Part</b> – Generator format rule (blue) that defines output pattern.</li>
  </ul>

  <div class="main-section-title">Format Rule</div>
  <p><b>TYPE;SIZE_OR_RANGE;CASE</b></p>

  <ul>
    <li><b>TYPE</b> – Defines character set
      <ul>
        <li><code>ALPHA</code> → Letters only</li>
        <li><code>ALPHANUM</code> → Letters + numbers</li>
        <li><code>NUM</code> → Numbers only</li>
      </ul>
    </li>

    <li><b>SIZE_OR_RANGE</b> – Defines length or numeric limits
      <ul>
        <li><code>10</code> → length = 10</li>
        <li><code>+10</code> → positive number with 10 digits</li>
        <li><code>-7</code> → negative number with 7 digits</li>
        <li><code>6</code> → random sign number with 6 digits</li>
        <li><code>-6--6</code> → random number between -6 and 6</li>
        <li><code>0-1000</code> → random number between 0 and 1000</li>
      </ul>
    </li>

    <li><b>CASE (optional)</b>
      <ul>
        <li><code>UPPER</code> → uppercase result</li>
        <li><code>LOWER</code> → lowercase result</li>
        <li>If omitted → default mixed case</li>
      </ul>
    </li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Reads format rule.</li>
    <li>Generates random value according to type + size/range.</li>
    <li>Applies case formatting if specified.</li>
    <li>Stores result into variable.</li>
  </ul>

  <div class="main-section-title">Examples</div>
  <ul>

    <li>
      <b>Input:</b> <code>ALPHA;10;UPPER</code><br>
      <b>Output:</b> <code>BZVSSZKVET</code>
    </li>

    <li>
      <b>Input:</b> <code>ALPHANUM;7;UPPER</code><br>
      <b>Output:</b> <code>9QV4D24</code>
    </li>

    <li>
      <b>Input:</b> <code>ALPHANUM;7;LOWER</code><br>
      <b>Output:</b> <code>z455rnj</code>
    </li>

    <li>
      <b>Input:</b> <code>NUM;+10</code><br>
      <b>Output:</b> <code>8908470302</code>
    </li>

    <li>
      <b>Input:</b> <code>NUM;-7</code><br>
      <b>Output:</b> <code>-3190182</code>
    </li>

    <li>
      <b>Input:</b> <code>NUM;6</code><br>
      <b>Output:</b> <code>-71051</code> or <code>649201</code>
    </li>

    <li>
      <b>Input:</b> <code>NUM;-6--6</code><br>
      <b>Output:</b> <code>-7</code> or <code>6</code>
    </li>

  </ul>

  <div class="main-section-title">Use Cases</div>
  <ul>
    <li>Generating random test data.</li>
    <li>Creating dynamic usernames or IDs.</li>
    <li>Generating OTP or verification codes.</li>
    <li>Testing form validations.</li>
  </ul>
                                                                                  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/random value  generator.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Variable name must start with <code>##</code>.  
      Case option only applies to <code>ALPHA</code> and <code>ALPHANUM</code> types.
    </span>
  </div>
</div>
`
  ,
  'concat': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">concat</h1>

    <div class="main-section-title">Description</div>
    <p>
      Concatenates (appends) a value to a string variable.  
      The first value is a variable containing text, and the second value is the text to append.  
      After execution, the variable is updated with the combined result.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">concat</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Text to Append</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>concat</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the initial text (blue background). Example: <code>##message</code>.</li>
      <li><b>Third Part</b> – The text or value to append (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Appends the specified text or value to it.</li>
      <li>Updates the variable with the concatenated result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Building dynamic messages or strings.</li>
      <li>Appending user input to existing variables.</li>
      <li>Creating formatted text outputs in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>concat</code>  
        <b>Variable:</b> <code>##message</code> (contains <code>Hello</code>)  
        <b>Text to Append:</b> <code> World</code>
      </li>
      <li>Result: <code>##message</code> now contains <code>Hello World</code>.</li>

      <li>
        <b>Action:</b> <code>concat</code>  
        <b>Variable:</b> <code>##fileName</code> (contains <code>report</code>)  
        <b>Text to Append:</b> <code>.pdf</code>
      </li>
      <li>Result: <code>##fileName</code> now contains <code>report.pdf</code>.</li>
    </ul>
                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/concat.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The variable should contain text.  
        Non-string values will be converted to text before concatenation.
      </span>
    </div>
  </div>
`,
  'reverseString': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">reverseString</h1>

    <div class="main-section-title">Description</div>
    <p>
      Reverses the characters of a string stored in a variable.  
      The action takes a string variable and updates it with the reversed value.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">reverseString</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>reverseString</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to be reversed (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Reverses the order of characters in the string.</li>
      <li>Updates the variable with the reversed result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Manipulating text for formatting or display.</li>
      <li>Creating reversed identifiers or codes.</li>
      <li>Simple string transformations in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>reverseString</code>  
        <b>Variable:</b> <code>##word</code> (contains <code>hello</code>)
      </li>
      <li>Result: <code>##word</code> now contains <code>olleh</code>.</li>

      <li>
        <b>Action:</b> <code>reverseString</code>  
        <b>Variable:</b> <code>##value</code> (contains <code>12345</code>)
      </li>
      <li>Result: <code>##value</code> now contains <code>54321</code>.</li>
    </ul>
                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/reverse.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The variable must contain a string value.  
        If the value is numeric, it will be treated as text before reversing.
      </span>
    </div>
  </div>
`
  , 'substringCount': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">substringCount</h1>

    <div class="main-section-title">Description</div>
    <p>
      Counts the number of occurrences of a specified substring within a string variable.  
      The action evaluates the text stored in the variable and determines how many times the given substring appears.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">substringCount</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Substring</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>substringCount</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the source text (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The substring to search for (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Searches the string for occurrences of the specified substring.</li>
      <li>Counts how many times the substring appears.</li>
      <li>Stores or returns the resulting count based on system behavior.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Analyzing text frequency or patterns.</li>
      <li>Counting keywords or tokens in strings.</li>
      <li>Validating input based on expected occurrences.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>substringCount</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>banana</code>)  
        <b>Substring:</b> <code>an</code>
      </li>
      <li>Result: The substring <code>an</code> appears <code>2</code> times.</li>

      <li>
        <b>Action:</b> <code>substringCount</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>hello hello</code>)  
        <b>Substring:</b> <code>hello</code>
      </li>
      <li>Result: The substring <code>hello</code> appears <code>2</code> times.</li>
    </ul>
                                                                                              <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string substeig count.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The search is case-sensitive unless otherwise specified.  
        If the substring is empty, the result may be <code>0</code> or undefined based on system rules.
      </span>
    </div>
  </div>
`
  , 'replace': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">replace</h1>

    <div class="main-section-title">Description</div>
    <p>
      Replaces occurrences of a specified substring within a string variable with a new value.  
      The action updates the variable by substituting the target text with the replacement text.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">replace</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Target Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>replace</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the original text (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The substring to be replaced (blue background).</li>
      <li><b>Fourth Part</b> – The text that will replace the target substring (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Searches for the specified target text.</li>
      <li>Replaces matching occurrences with the replacement text.</li>
      <li>Updates the variable with the modified result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Cleaning or formatting text.</li>
      <li>Replacing placeholders with dynamic values.</li>
      <li>Correcting or normalizing string content.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>replace</code>  
        <b>Variable:</b> <code>##message</code> (contains <code>Hello World</code>)  
        <b>Target Text:</b> <code>World</code>  
        <b>Replacement Text:</b> <code>There</code>
      </li>
      <li>Result: <code>##message</code> now contains <code>Hello There</code>.</li>

      <li>
        <b>Action:</b> <code>replace</code>  
        <b>Variable:</b> <code>##path</code> (contains <code>/user/home/docs</code>)  
        <b>Target Text:</b> <code>/</code>  
        <b>Replacement Text:</b> <code>-</code>
      </li>
      <li>Result: <code>##path</code> now contains <code>-user-home-docs</code>.</li>
    </ul>
    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Replacement is case-sensitive unless otherwise specified.  
        All matching occurrences may be replaced depending on system behavior.
      </span>
    </div>
  </div>
`
  , 'replaceAll': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">replaceAll</h1>

    <div class="main-section-title">Description</div>
    <p>
      Replaces all occurrences of a specified substring within a string variable with a new value.  
      Unlike <code>replace</code>, this action ensures every matching instance is updated.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">replaceAll</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Target Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>replaceAll</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the original text (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The substring to be replaced (blue background).</li>      
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Searches the string for all occurrences of the target text.</li>
      <li>Replaces every match with the replacement text.</li>
      <li>Updates the variable with the fully modified result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Bulk text cleanup or normalization.</li>
      <li>Replacing all placeholders in templates.</li>
      <li>Standardizing formats or delimiters.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>replaceAll</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>apple banana apple</code>)  
        <b>Target Text:</b> <code>apple</code>  
        <b>Replacement Text:</b> <code>orange</code>
      </li>
      <li>Result: <code>##sentence</code> now contains <code>orange banana orange</code>.</li>

      <li>
        <b>Action:</b> <code>replaceAll</code>  
        <b>Variable:</b> <code>##csv</code> (contains <code>a,b,c,d</code>)  
        <b>Target Text:</b> <code>,</code>  
        <b>Replacement Text:</b> <code>|</code>
      </li>
      <li>Result: <code>##csv</code> now contains <code>a|b|c|d</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The operation is case-sensitive unless otherwise specified.  
        All occurrences of the target text are replaced; none are skipped.
      </span>
    </div>
  </div>
`
  , 'replaceRegex': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">replaceRegex</h1>

    <div class="main-section-title">Description</div>
    <p>
      Replaces parts of a string variable that match a specified regular expression with a new value.  
      This allows advanced pattern-based text replacement using regex rules.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">replaceRegex</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Regex Pattern</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>replaceRegex</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the original text (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The regular expression pattern to match (blue background). Example: <code>\\d+</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Applies the regular expression pattern to find matches in the string.</li>
      <li>Replaces all matches with the specified replacement text.</li>
      <li>Updates the variable with the modified result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Replacing patterns such as numbers, emails, or dates within text.</li>
      <li>Cleaning or normalizing text dynamically.</li>
      <li>Performing complex search-and-replace operations in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>replaceRegex</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>My phone: 123-456-7890</code>)  
        <b>Regex Pattern:</b> <code>\\d{3}-\\d{3}-\\d{4}</code>  
        <b>Replacement Text:</b> <code>[REDACTED]</code>
      </li>
      <li>Result: <code>##text</code> now contains <code>My phone: [REDACTED]</code>.</li>

      <li>
        <b>Action:</b> <code>replaceRegex</code>  
        <b>Variable:</b> <code>##data</code> (contains <code>abc123def456</code>)  
        <b>Regex Pattern:</b> <code>\\d+</code>  
        <b>Replacement Text:</b> <code>#</code>
      </li>
      <li>Result: <code>##data</code> now contains <code>abc#def#</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The regex pattern must follow standard regex syntax.  
        Be careful with special characters; escaping may be required.  
        All matches in the string are replaced unless regex flags are specified differently.
      </span>
    </div>
  </div>
`
  , 'trim': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">trim</h1>

    <div class="main-section-title">Description</div>
    <p>
      Removes leading and trailing whitespace characters from a string variable.  
      After execution, the variable is updated with the trimmed text.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">trim</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>trim</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to trim (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Removes all spaces, tabs, and other whitespace characters at the beginning and end of the string.</li>
      <li>Updates the variable with the trimmed result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Cleaning user input by removing unnecessary spaces.</li>
      <li>Preparing strings for comparison or validation.</li>
      <li>Standardizing text formatting in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>trim</code>  
        <b>Variable:</b> <code>##name</code> (contains <code>  John Doe  </code>)
      </li>
      <li>Result: <code>##name</code> now contains <code>John Doe</code>.</li>

      <li>
        <b>Action:</b> <code>trim</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>\tHello World \n</code>)
      </li>
      <li>Result: <code>##text</code> now contains <code>Hello World</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only whitespace at the start and end of the string is removed.  
        Spaces between words remain unchanged.
      </span>
    </div>
  </div>
`
  , 'trimLeft': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">trimLeft</h1>

    <div class="main-section-title">Description</div>
    <p>
      Removes all leading whitespace characters (spaces, tabs, etc.) from a string variable.  
      After execution, the variable is updated with the text trimmed only at the start.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">trimLeft</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>trimLeft</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to trim (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Removes all whitespace characters at the beginning of the string.</li>
      <li>Leaves trailing whitespace intact.</li>
      <li>Updates the variable with the resulting text.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Cleaning user input when only leading spaces are unwanted.</li>
      <li>Preparing strings for alignment or formatting.</li>
      <li>Standardizing text in automation flows without removing trailing spaces.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>trimLeft</code>  
        <b>Variable:</b> <code>##name</code> (contains <code>   John Doe  </code>)
      </li>
      <li>Result: <code>##name</code> now contains <code>John Doe  </code>.</li>

      <li>
        <b>Action:</b> <code>trimLeft</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>\t Hello World \n</code>)
      </li>
      <li>Result: <code>##text</code> now contains <code>Hello World \n</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only whitespace at the start of the string is removed.  
        Trailing spaces or tabs remain unchanged.
      </span>
    </div>
  </div>
`
  , 'trimRight': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">trimRight</h1>

    <div class="main-section-title">Description</div>
    <p>
      Removes all trailing whitespace characters (spaces, tabs, etc.) from a string variable.  
      After execution, the variable is updated with the text trimmed only at the end.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">trimRight</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>trimRight</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to trim (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Removes all whitespace characters at the end of the string.</li>
      <li>Leaves leading whitespace intact.</li>
      <li>Updates the variable with the resulting text.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Cleaning user input when only trailing spaces are unwanted.</li>
      <li>Preparing strings for alignment or formatting without affecting leading spaces.</li>
      <li>Standardizing text in automation flows while preserving indentation at the start.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>trimRight</code>  
        <b>Variable:</b> <code>##name</code> (contains <code>  John Doe   </code>)
      </li>
      <li>Result: <code>##name</code> now contains <code>  John Doe</code>.</li>

      <li>
        <b>Action:</b> <code>trimRight</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>\tHello World \n</code>)
      </li>
      <li>Result: <code>##text</code> now contains <code>\tHello World</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only whitespace at the end of the string is removed.  
        Leading spaces or tabs remain unchanged.
      </span>
    </div>
  </div>
`
  ,
  'toUpper': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">toUpper</h1>

    <div class="main-section-title">Description</div>
    <p>
      Converts all characters in a string variable to uppercase.  
      After execution, the variable is updated with the fully capitalized text.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">toUpper</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>toUpper</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to convert (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Converts all lowercase letters to uppercase.</li>
      <li>Leaves non-alphabetic characters unchanged.</li>
      <li>Updates the variable with the uppercase result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Standardizing text for display or comparison.</li>
      <li>Preparing inputs for case-sensitive operations.</li>
      <li>Formatting data in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>toUpper</code>  
        <b>Variable:</b> <code>##name</code> (contains <code>John Doe</code>)
      </li>
      <li>Result: <code>##name</code> now contains <code>JOHN DOE</code>.</li>

      <li>
        <b>Action:</b> <code>toUpper</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>hello world 123</code>)
      </li>
      <li>Result: <code>##text</code> now contains <code>HELLO WORLD 123</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only alphabetic characters are affected. Numbers, symbols, and whitespace remain unchanged.
      </span>
    </div>
  </div>
`
  , 'toLower': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">toLower</h1>

    <div class="main-section-title">Description</div>
    <p>
      Converts all characters in a string variable to lowercase.  
      After execution, the variable is updated with the fully lowercase text.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">toLower</div>
      <div class="press-key-key">Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>toLower</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to convert (blue background). Example: <code>##text</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Converts all uppercase letters to lowercase.</li>
      <li>Leaves non-alphabetic characters unchanged.</li>
      <li>Updates the variable with the lowercase result.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Standardizing text for display, comparison, or validation.</li>
      <li>Preparing inputs for case-insensitive operations.</li>
      <li>Formatting data consistently in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>toLower</code>  
        <b>Variable:</b> <code>##name</code> (contains <code>John Doe</code>)
      </li>
      <li>Result: <code>##name</code> now contains <code>john doe</code>.</li>

      <li>
        <b>Action:</b> <code>toLower</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>HELLO WORLD 123</code>)
      </li>
      <li>Result: <code>##text</code> now contains <code>hello world 123</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only alphabetic characters are affected. Numbers, symbols, and whitespace remain unchanged.
      </span>
    </div>
  </div>
`,
  'indexOf': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">indexOf</h1>

    <div class="main-section-title">Description</div>
    <p>
      Finds the position of the first occurrence of a specified substring within a string variable.  
      The result is the index (starting from 0) of the substring, or <code>-1</code> if the substring is not found.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">indexOf</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Substring</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>indexOf</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the string to search in (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The substring to find (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Searches for the first occurrence of the specified substring.</li>
      <li>Returns the zero-based index of the substring or <code>-1</code> if not found.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Locating a specific word or character within a string.</li>
      <li>Validating the presence or position of content.</li>
      <li>Performing conditional operations based on substring position.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>indexOf</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>Hello World</code>)  
        <b>Substring:</b> <code>World</code>
      </li>
      <li>Result: <code>5</code> (position of "World" in the string).</li>

      <li>
        <b>Action:</b> <code>indexOf</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>banana</code>)  
        <b>Substring:</b> <code>na</code>
      </li>
      <li>Result: <code>2</code> (first occurrence of "na").</li>

      <li>
        <b>Action:</b> <code>indexOf</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>banana</code>)  
        <b>Substring:</b> <code>xyz</code>
      </li>
      <li>Result: <code>-1</code> (substring not found).</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The search is case-sensitive unless otherwise specified.  
        Indexing starts at 0 for the first character of the string.
      </span>
    </div>
  </div>
`
  , 'lastIndexOf': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">lastIndexOf</h1>

    <div class="main-section-title">Description</div>
    <p>
      Finds the position of the last occurrence of a specified substring within a string variable.  
      The result is the index (starting from 0) of the substring, or <code>-1</code> if the substring is not found.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">lastIndexOf</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Substring</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>lastIndexOf</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the string to search in (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The substring to find (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Searches for the last occurrence of the specified substring.</li>
      <li>Returns the zero-based index of the substring or <code>-1</code> if not found.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Finding the final position of a word, character, or pattern in a string.</li>
      <li>Useful in parsing strings from the end or extracting substrings.</li>
      <li>Performing conditional operations based on the last occurrence of content.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>lastIndexOf</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>Hello World World</code>)  
        <b>Substring:</b> <code>World</code>
      </li>
      <li>Result: <code>12</code> (position of the last "World" in the string).</li>

      <li>
        <b>Action:</b> <code>lastIndexOf</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>banana</code>)  
        <b>Substring:</b> <code>na</code>
      </li>
      <li>Result: <code>4</code> (last occurrence of "na").</li>

      <li>
        <b>Action:</b> <code>lastIndexOf</code>  
        <b>Variable:</b> <code>##sentence</code> (contains <code>banana</code>)  
        <b>Substring:</b> <code>xyz</code>
      </li>
      <li>Result: <code>-1</code> (substring not found).</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The search is case-sensitive unless otherwise specified.  
        Indexing starts at 0 for the first character of the string.
      </span>
    </div>
  </div>
`
  , 'repeat': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">repeat</h1>

    <div class="main-section-title">Description</div>
    <p>
      Repeats the content of a string variable a specified number of times.  
      After execution, the variable is updated with the repeated string concatenated together.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">repeat</div>
      <div class="press-key-key">Variable</div>
      <div class="press-key-key">Number of Times</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>repeat</b>) with maroon background.</li>
      <li><b>Second Part</b> – The variable containing the text to repeat (blue background). Example: <code>##text</code>.</li>
      <li><b>Third Part</b> – The number of times to repeat the string (blue background). Must be a non-negative integer.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <ul>
      <li>Retrieves the current value of the variable.</li>
      <li>Concatenates the string with itself the specified number of times.</li>
      <li>Updates the variable with the resulting repeated string.</li>
    </ul>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Generating repeated patterns or sequences.</li>
      <li>Creating placeholder text or padding.</li>
      <li>Building formatted outputs dynamically in automation flows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>repeat</code>  
        <b>Variable:</b> <code>##text</code> (contains <code>Hi</code>)  
        <b>Number of Times:</b> <code>3</code>
      </li>
      <li>Result: <code>##text</code> now contains <code>HiHiHi</code>.</li>

      <li>
        <b>Action:</b> <code>repeat</code>  
        <b>Variable:</b> <code>##pattern</code> (contains <code>-</code>)  
        <b>Number of Times:</b> <code>5</code>
      </li>
      <li>Result: <code>##pattern</code> now contains <code>-----</code>.</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The number of times must be a non-negative integer.  
        A value of 0 results in an empty string.
      </span>
    </div>
  </div>
`,
  'assert-text-contains': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">assert-text-contains</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts that a specified string variable, element text, or value contains a given substring.  
    The test immediately fails if the substring is not found, making it useful for validation in automation flows.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-text-contains</div>
    <div class="press-key-key">Variable or Element</div>
    <div class="press-key-key">Substring</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>assert-text-contains</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable containing the text or element locator to check (blue background). Example: <code>##message</code> or <code>#statusLabel</code>.</li>
    <li><b>Third Part</b> – The substring expected to exist within the text (blue background).</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Retrieves the value of the variable or the text of the element.</li>
    <li>Checks if the retrieved text contains the specified substring.</li>
    <li>Immediately fails the test if the substring is not found; passes if it exists.</li>
  </ul>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating dynamic messages or notifications.</li>
    <li>Ensuring user input appears correctly in UI elements.</li>
    <li>Checking for presence of specific content in logs, labels, or responses.</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-text-contains</code>  
      <b>Variable:</b> <code>##message</code> (contains <code>Hello World</code>)  
      <b>Substring:</b> <code>Hello</code>
    </li>
    <li>Result: Passes because <code>##message</code> contains <code>Hello</code>.</li>

    <li>
      <b>Action:</b> <code>assert-text-contains</code>  
      <b>Element:</b> <code>#statusLabel</code> (displays <code>File uploaded successfully</code>)  
      <b>Substring:</b> <code>uploaded</code>
    </li>
    <li>Result: Passes because the element text contains <code>uploaded</code>.</li>
  </ul>
                                                                                                    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string asserted text contains.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Comparison is usually case-sensitive unless configured otherwise.  
      Ensure the variable or element actually contains text; non-string values are converted to text before checking.
    </span>
  </div>
</div>
`,
  'assert-texts': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">assert-texts</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts that multiple specified texts are present within a variable, element, or page content.  
    The test immediately fails if any of the expected texts are not found.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-texts</div>
    <div class="press-key-key">Variable or Element</div>
    <div class="press-key-key">Expected Texts</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>assert-texts</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable, element locator, or page reference containing the text (blue background). Example: <code>##response</code> or <code>#contentSection</code>.</li>
    <li><b>Third Part</b> – The list of expected texts to validate (blue background). Texts can be separated by commas or defined according to framework rules.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Retrieves the text from the variable, element, or page.</li>
    <li>Checks whether all specified expected texts are present.</li>
    <li>Immediately fails the test if any expected text is missing; passes only if all texts are found.</li>
  </ul>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating multiple messages or notifications at once.</li>
    <li>Ensuring several values appear in a summary or confirmation page.</li>
    <li>Checking response content in API or UI validations.</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-texts</code>  
      <b>Element:</b> <code>#summarySection</code>  
      <b>Expected Texts:</b> <code>Order Confirmed, Payment Successful, Thank You</code>
    </li>
    <li>
      Result: Passes only if all three texts are present within <code>#summarySection</code>.
    </li>

    <li>
      <b>Action:</b> <code>assert-texts</code>  
      <b>Variable:</b> <code>##apiResponse</code>  
      <b>Expected Texts:</b> <code>status, success, 200</code>
    </li>
    <li>
      Result: Passes only if all listed texts are found inside <code>##apiResponse</code>.
    </li>
  </ul>
                                                                                                      <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string asserted text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      All expected texts must be present for the assertion to pass.  
      Text comparison is usually case-sensitive unless configured otherwise.
    </span>
  </div>
</div>
`,
  'verify-text-contains': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">verify-text-contains</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies that a specified string variable, element text, or value contains a given substring.  
    The step passes if the substring is found and fails if it is not found.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-text-contains</div>
    <div class="press-key-key">Variable or Element</div>
    <div class="press-key-key">Substring</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>verify-text-contains</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable containing the text or element locator to check (blue background). Example: <code>##message</code> or <code>#statusLabel</code>.</li>
    <li><b>Third Part</b> – The substring expected to exist within the text (blue background).</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Retrieves the value of the variable or the text of the element.</li>
    <li>Checks whether the retrieved text contains the specified substring.</li>
    <li>Passes the step if the substring is found; fails if it is not found.</li>
  </ul>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating dynamic messages or notifications.</li>
    <li>Ensuring partial content appears in UI elements.</li>
    <li>Checking logs, responses, or labels for specific keywords.</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-text-contains</code>  
      <b>Variable:</b> <code>##message</code> (contains <code>Hello World</code>)  
      <b>Substring:</b> <code>Hello</code>
    </li>
    <li>
      Result: Passes because <code>Hello</code> exists within <code>##message</code>.
    </li>

    <li>
      <b>Action:</b> <code>verify-text-contains</code>  
      <b>Element:</b> <code>#statusLabel</code> (displays <code>File uploaded successfully</code>)  
      <b>Substring:</b> <code>uploaded</code>
    </li>
    <li>
      Result: Passes if the element text contains <code>uploaded</code>; otherwise fails.
    </li>
  </ul>
                                                                                                        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string verify text cotains.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Comparison is usually case-sensitive unless configured otherwise.  
      Ensure the variable or element contains text before verification.
    </span>
  </div>
</div>
`,
  'verify-texts': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">verify-texts</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies that multiple specified texts are present within a variable, element, or page content.  
    The step passes if all expected texts are found and fails if any text is missing.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-texts</div>
    <div class="press-key-key">Variable or Element</div>
    <div class="press-key-key">Expected Texts</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>verify-texts</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable, element locator, or page reference containing the text (blue background). Example: <code>##response</code> or <code>#contentSection</code>.</li>
    <li><b>Third Part</b> – The list of expected texts to validate (blue background). Texts can be separated by commas or defined according to framework rules.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Retrieves the text from the variable, element, or page.</li>
    <li>Checks whether all specified expected texts are present.</li>
    <li>Passes the step if all texts are found; fails if any expected text is missing.</li>
  </ul>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating multiple messages or notifications in one step.</li>
    <li>Ensuring summary pages contain all required information.</li>
    <li>Checking response content in UI or API validations.</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-texts</code>  
      <b>Element:</b> <code>#summarySection</code>  
      <b>Expected Texts:</b> <code>Order Confirmed, Payment Successful, Thank You</code>
    </li>
    <li>
      Result: Passes only if all three texts are present within <code>#summarySection</code>.
    </li>

    <li>
      <b>Action:</b> <code>verify-texts</code>  
      <b>Variable:</b> <code>##apiResponse</code>  
      <b>Expected Texts:</b> <code>status, success, 200</code>
    </li>
    <li>
      Result: Passes if all listed texts are found inside <code>##apiResponse</code>; otherwise fails.
    </li>
  </ul>
                                                                                                          <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string verify text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      All expected texts must be present for the verification to pass.  
      Text comparison is usually case-sensitive unless configured otherwise.
    </span>
  </div>
</div>
`,
  'string-to-object': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">string-to-object</h1>

  <div class="main-section-title">Description</div>
  <p>
    Converts a JSON-formatted string into an object.  
    This allows structured data within a string variable to be accessed and used as an object in automation workflows.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">string-to-object</div>
    <div class="press-key-key">Source Variable (String)</div>
    <div class="press-key-key">Target Variable (Object)</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>string-to-object</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable containing the JSON string (blue background). Example: <code>##jsonString</code>.</li>
    <li><b>Third Part</b> – The variable where the parsed object will be stored (blue background). Example: <code>##jsonObject</code>.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <ul>
    <li>Retrieves the JSON-formatted string from the source variable.</li>
    <li>Parses the string into an object.</li>
    <li>Stores the resulting object in the target variable for further use.</li>
  </ul>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Parsing API responses stored as strings.</li>
    <li>Extracting values from structured JSON content.</li>
    <li>Converting serialized data into usable object format.</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>string-to-object</code>  
      <b>Source Variable:</b> <code>##apiResponse</code> (contains <code>{"status":"success","code":200}</code>)  
      <b>Target Variable:</b> <code>##responseObject</code>
    </li>
    <li>
      Result: <code>##responseObject</code> becomes an object with properties  
      <code>status = "success"</code> and <code>code = 200</code>.
    </li>
  </ul>
                                                                                                            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/string to object.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The source string must be valid JSON format.  
      If the string is not properly formatted, the conversion will fail.
    </span>
  </div>
</div>
`
};
export const logicMap: { [key: string]: string } = {
  'if-equals-to': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">if-equals-to</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a conditional check to compare two values.  
      If the first value equals the second value, all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      If the condition is false, the block is skipped.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">if-equals-to</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>if-equals-to</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value or condition (blue background). Can be text or variable.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be text, number, or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first value to the second value.</li>
        <li>If true, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If false, skips the block and continues execution after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Perform actions only when a variable equals a specific value.</li>
      <li>Conditional flows for user input, status flags, or dropdown selections.</li>
      <li>Nested conditions using multiple <code>if-equals-to</code> blocks.</li>
    </ul>

    <div class="main-section-title">Example</div>
<ul>
  <li>
    <b>Action:</b> <code>if-equals-to</code>  
    <b>First Value:</b> <code>test</code>  
    <b>Second Value:</b> <code>test</code>
  </li>
  <li>Result: <code>True</code> – The values are equal, so all actions inside this block execute until <code>close-statement</code>.</li>

  <li>
    <b>Action:</b> <code>if-equals-to</code>  
    <b>First Value:</b> <code>test</code>  
    <b>Second Value:</b> <code>test1</code>
  </li>
  <li>Result: <code>False</code> – The values are not equal, so the block is skipped and execution continues after <code>close-statement</code>.</li>

  <li>
    <b>Action:</b> <code>if-equals-to</code>  
    <b>First Value:</b> <code>##myValue</code> (variable containing <code>123</code>)  
    <b>Second Value:</b> <code>123</code>
  </li>
  <li>Result: <code>True</code> – The variable value matches the second value, so the block executes until <code>close-statement</code>.</li>
</ul>
    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/if equals to.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>if-equals-to</code> block must be closed with a <code>close-statement</code>.  
        The first value can be text or a variable; the second value can be text, number, or variable.  
        True condition executes the block; false condition skips it.
      </span>
    </div>
  </div>
`,
  'if-equals-ignore-case': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">if-equals-ignore-case</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a conditional check to compare two values while ignoring letter case.  
      If the first value equals the second value (case-insensitive), all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      If the condition is false, the block is skipped.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">if-equals-ignore-case</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>if-equals-ignore-case</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value or condition (blue background). Can be text or variable.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be text, number, or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first value to the second value without considering letter case.</li>
        <li>If true, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If false, skips the block and continues execution after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Comparing user input where letter case should not matter.</li>
      <li>Validating text values such as status, role, or type.</li>
      <li>Conditional logic where uppercase and lowercase should be treated the same.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>if-equals-ignore-case</code>  
        <b>First Value:</b> <code>test</code>  
        <b>Second Value:</b> <code>TEST</code>
      </li>
      <li>Result: <code>True</code> – Case is ignored, so all actions inside this block execute until <code>close-statement</code>.</li>

      <li>
        <b>Action:</b> <code>if-equals-ignore-case</code>  
        <b>First Value:</b> <code>Test</code>  
        <b>Second Value:</b> <code>test1</code>
      </li>
      <li>Result: <code>False</code> – Values are not equal, so the block is skipped.</li>

      <li>
        <b>Action:</b> <code>if-equals-ignore-case</code>  
        <b>First Value:</b> <code>##myValue</code> (variable containing <code>ABC</code>)  
        <b>Second Value:</b> <code>abc</code>
      </li>
      <li>Result: <code>True</code> – Variable value matches ignoring case, so the block executes.</li>
    </ul>
        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/if equals to ignore case.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>if-equals-ignore-case</code> block must be closed with a <code>close-statement</code>.  
        Comparison ignores uppercase and lowercase differences.  
        True condition executes the block; false condition skips it.
      </span>
    </div>
  </div>
`  ,
  'if-greater-than': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">if-greater-than</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a conditional check to compare two numeric values.  
      If the first value is greater than the second value, all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      If the condition is false, the block is skipped.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">if-greater-than</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>if-greater-than</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first numeric value or variable (blue background).</li>
      <li><b>Third Part</b> – The second numeric value or variable (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first numeric value with the second numeric value.</li>
        <li>If the first value is greater than the second value, the condition is true and the block executes.</li>
        <li>If the first value is less than or equal to the second value, the block is skipped.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating numeric thresholds or limits.</li>
      <li>Conditional flows based on counts, scores, or quantities.</li>
      <li>Executing logic only when a value exceeds another value.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>if-greater-than</code>  
        <b>First Value:</b> <code>5</code>  
        <b>Second Value:</b> <code>2</code>
      </li>
      <li>Result: <code>True</code> – Since 5 is greater than 2, all actions inside this block execute until <code>close-statement</code>.</li>

      <li>
        <b>Action:</b> <code>if-greater-than</code>  
        <b>First Value:</b> <code>2</code>  
        <b>Second Value:</b> <code>5</code>
      </li>
      <li>Result: <code>False</code> – Since 2 is not greater than 5, the block is skipped.</li>

      <li>
        <b>Action:</b> <code>if-greater-than</code>  
        <b>First Value:</b> <code>##myValue</code> (variable containing <code>10</code>)  
        <b>Second Value:</b> <code>5</code>
      </li>
      <li>Result: <code>True</code> – Variable value is greater than the second value, so the block executes.</li>
    </ul>
            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/if greaterthan.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>if-greater-than</code> block must be closed with a <code>close-statement</code>.  
        This action works only with numeric values or variables containing numbers.  
        True condition executes the block; false condition skips it.
      </span>
    </div>
  </div>
`  ,
  'if-less-than': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">if-less-than</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a conditional check to compare two numeric values.  
      If the first value is less than the second value, all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      If the condition is false, the block is skipped.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">if-less-than</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>if-less-than</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first numeric value or variable (blue background).</li>
      <li><b>Third Part</b> – The second numeric value or variable (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first numeric value with the second numeric value.</li>
        <li>If the first value is less than the second value, the condition is true and the block executes.</li>
        <li>If the first value is greater than or equal to the second value, the block is skipped.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating minimum values or thresholds.</li>
      <li>Conditional logic based on counts, scores, or limits.</li>
      <li>Executing actions only when a value is below another value.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>if-less-than</code>  
        <b>First Value:</b> <code>2</code>  
        <b>Second Value:</b> <code>5</code>
      </li>
      <li>Result: <code>True</code> – Since 2 is less than 5, all actions inside this block execute until <code>close-statement</code>.</li>

      <li>
        <b>Action:</b> <code>if-less-than</code>  
        <b>First Value:</b> <code>5</code>  
        <b>Second Value:</b> <code>2</code>
      </li>
      <li>Result: <code>False</code> – Since 5 is not less than 2, the block is skipped.</li>

      <li>
        <b>Action:</b> <code>if-less-than</code>  
        <b>First Value:</b> <code>##myValue</code> (variable containing <code>3</code>)  
        <b>Second Value:</b> <code>10</code>
      </li>
      <li>Result: <code>True</code> – Variable value is less than the second value, so the block executes.</li>
    </ul>
                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/if element lessthan.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>if-less-than</code> block must be closed with a <code>close-statement</code>.  
        This action works only with numeric values or variables containing numbers.  
        True condition executes the block; false condition skips it.
      </span>
    </div>
  </div>
`  ,
  'if-element-present': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">if-element-present</h1>

    <div class="main-section-title">Description</div>
    <p>
      Performs a conditional check to verify whether a specified element is present on the page.  
      If the element is found, all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      If the element is not present, the block is skipped.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">if-element-present</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>if-element-present</b>) with maroon background.</li>
      <li><b>Second Part</b> – The element locator to check (gray background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Checks whether the specified element exists in the current page or context.</li>
        <li>If the element is present, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If the element is not present, skips the block and continues execution after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Handling optional or dynamic elements.</li>
      <li>Executing actions only when a button, message, or section exists.</li>
      <li>Preventing failures when elements may or may not appear.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>if-element-present</code>  
        <b>Element:</b> <code>#loginButton</code>
      </li>
      <li>
        Result: <code>True</code> – The element is present, so all actions inside this block execute until <code>close-statement</code>.
      </li>
      <li>
        If the element is not found, the condition is <code>False</code> and the block is skipped.
      </li>
    </ul>
                    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/if element present.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>if-element-present</code> block must be closed with a <code>close-statement</code>.  
        Ensure the element locator is correct and refers to the current page or iframe context.
      </span>
    </div>
  </div>
`  ,
  'else': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">else</h1>

    <div class="main-section-title">Description</div>
    <p>
      Defines an alternative execution block for an existing <code>if</code> condition.  
      The <code>else</code> block executes only when the associated <code>if</code> condition evaluates to false.  
      This action is valid only when used after an <code>if</code> block and its <code>close-statement</code>.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">else</div>
    </div>
    <ul>
      <li><b>Only Part</b> – The action (<b>else</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Checks the result of the immediately preceding <code>if</code> condition.</li>
        <li>If the <code>if</code> condition was false, executes all actions inside the <code>else</code> block.</li>
        <li>If the <code>if</code> condition was true, skips the <code>else</code> block.</li>
        <li>Execution of the <code>else</code> block continues until a <code>close-statement</code> is reached.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Executing alternative logic when an <code>if</code> condition fails.</li>
      <li>Handling success vs failure scenarios.</li>
      <li>Providing fallback actions in conditional workflows.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Flow:</b><br/>
        <code>if-condition</code> → <code>close-statement</code> → <code>else</code> → <code>close-statement</code>
      </li>
      <li>
        If the <code>if</code> condition is <code>false</code>, all actions inside the <code>else</code> block execute until the second <code>close-statement</code>.
      </li>
      <li>
        If the <code>if</code> condition is <code>true</code>, the <code>else</code> block is skipped.
      </li>
    </ul>
                        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/else.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The <code>else</code> action must always follow an <code>if</code> block and its <code>close-statement</code>.  
        Every <code>else</code> block must also be closed with a <code>close-statement</code>.  
        Using <code>else</code> without a preceding <code>if</code> condition is invalid.
      </span>
    </div>
  </div>
`  ,
  'else-if-equals-to': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">else-if-equals-to</h1>

    <div class="main-section-title">Description</div>
    <p>
      Defines an additional conditional check that is evaluated only if the preceding <code>if</code> condition is false.  
      If the values in this condition are equal, all actions inside this block will execute until a <code>close-statement</code> action is encountered.  
      This action is valid only when used after an <code>if</code> block and its <code>close-statement</code>.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">else-if-equals-to</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>else-if-equals-to</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value or condition (blue background). Can be text or variable.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be text, number, or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Evaluates this condition only if the previous <code>if</code> condition was false.</li>
        <li>Compares the first value to the second value.</li>
        <li>If true, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If false, skips this block and continues to the next <code>else-if</code> or <code>else</code> block if present.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Handling multiple conditional paths.</li>
      <li>Checking alternate values when the main <code>if</code> condition fails.</li>
      <li>Creating chained conditional logic.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Flow:</b><br/>
        <code>if-equals-to</code> → <code>close-statement</code> → <code>else-if-equals-to</code> → <code>close-statement</code> → <code>else</code>
      </li>

      <li>
        <b>Action:</b> <code>else-if-equals-to</code>  
        <b>First Value:</b> <code>status</code>  
        <b>Second Value:</b> <code>pending</code>
      </li>

      <li>
        Result: <code>True</code> – Since the previous <code>if</code> was false and these values are equal, all actions inside this block execute until <code>close-statement</code>.
      </li>
    </ul>
                            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/else if equal to.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The <code>else-if-equals-to</code> action must always follow an <code>if</code> block and its <code>close-statement</code>.  
        It behaves the same as <code>if-equals-to</code> but runs only when the previous condition fails.  
        Every <code>else-if-equals-to</code> block must be closed with a <code>close-statement</code>.
      </span>
    </div>
  </div>
`  ,
  'else-if-equals-ignore-case': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">else-if-equals-ignore-case</h1>

    <div class="main-section-title">Description</div>
    <p>
      Defines an alternative conditional check that compares two values while ignoring letter case.  
      This condition is evaluated only if the preceding <code>if</code> condition is false.  
      If the values are equal (case-insensitive), all actions inside this block will execute until a <code>close-statement</code> action is encountered.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">else-if-equals-ignore-case</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>else-if-equals-ignore-case</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value or condition (blue background). Can be text or variable.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be text, number, or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Runs this condition only if the previous <code>if</code> condition evaluated to false.</li>
        <li>Compares the two values without considering uppercase or lowercase differences.</li>
        <li>If true, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If false, skips this block and moves to the next <code>else-if</code> or <code>else</code> block.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Handling alternate text conditions where case should not matter.</li>
      <li>Validating user input such as roles, status, or type.</li>
      <li>Building multi-branch conditional logic.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Flow:</b><br/>
        <code>if-equals-to</code> → <code>close-statement</code> → <code>else-if-equals-ignore-case</code> → <code>close-statement</code> → <code>else</code>
      </li>

      <li>
        <b>Action:</b> <code>else-if-equals-ignore-case</code>  
        <b>First Value:</b> <code>status</code>  
        <b>Second Value:</b> <code>ACTIVE</code>
      </li>

      <li>
        Result: <code>True</code> – If the previous <code>if</code> condition failed and the values match ignoring case, all actions inside this block execute until <code>close-statement</code>.
      </li>
    </ul>
                                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/else if equal to ignore case.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The <code>else-if-equals-ignore-case</code> action must always follow an <code>if</code> block and its <code>close-statement</code>.  
        Case differences are ignored during comparison.  
        Every <code>else-if-equals-ignore-case</code> block must be closed with a <code>close-statement</code>.
      </span>
    </div>
  </div>
`  ,
  'else-if-element-present': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">else-if-element-present</h1>

    <div class="main-section-title">Description</div>
    <p>
      Defines an alternative conditional check to verify whether a specified element is present on the page.  
      This condition is evaluated only if the preceding <code>if</code> condition is false.  
      If the element is found, all actions inside this block will execute until a <code>close-statement</code> action is encountered.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">else-if-element-present</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>else-if-element-present</b>) with maroon background.</li>
      <li><b>Second Part</b> – The element locator to check (gray background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Evaluates this condition only if the previous <code>if</code> condition was false.</li>
        <li>Checks whether the specified element exists in the current page or context.</li>
        <li>If the element is present, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If the element is not present, skips this block and continues to the next <code>else-if</code> or <code>else</code> block.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Handling alternate UI states when the primary condition fails.</li>
      <li>Checking for fallback or secondary elements.</li>
      <li>Building robust conditional UI automation.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Flow:</b><br/>
        <code>if-element-present</code> → <code>close-statement</code> → <code>else-if-element-present</code> → <code>close-statement</code> → <code>else</code>
      </li>

      <li>
        <b>Action:</b> <code>else-if-element-present</code>  
        <b>Element:</b> <code>#errorMessage</code>
      </li>

      <li>
        Result: <code>True</code> – If the previous <code>if</code> condition failed and this element is present, all actions inside this block execute until <code>close-statement</code>.
      </li>
    </ul>
                                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/else if element present.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The <code>else-if-element-present</code> action must always follow an <code>if</code> block and its <code>close-statement</code>.  
        Ensure the element locator is valid for the current page or iframe context.  
        Every <code>else-if-element-present</code> block must be closed with a <code>close-statement</code>.
      </span>
    </div>
  </div>
`  ,
  'break': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">break</h1>

    <div class="main-section-title">Description</div>
    <p>
      Immediately exits the current loop block.  
      Execution continues after the corresponding <code>close-statement</code> of the loop.  
      This action is only applicable inside loop blocks such as <code>loop-count</code>, <code>loop-equals</code>, <code>loop-less-than</code>, or similar loops.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">break</div>
    </div>
    <ul>
      <li><b>Only Part</b> – The action (<b>break</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Stops execution of the current loop immediately.</li>
        <li>Skips all remaining actions inside the loop.</li>
        <li>Continues execution after the <code>close-statement</code> of the loop.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Exiting a loop when a condition is met before reaching the total iterations.</li>
      <li>Stopping repetitive actions if a certain element appears or a condition is fulfilled.</li>
      <li>Preventing unnecessary loop iterations to save execution time.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Inside Loop Count:</b>  
        <code>loop-count</code> → (actions) → <code>break</code> → <code>close-statement</code>  
        Result: The loop stops immediately when <code>break</code> is reached, and execution continues after <code>close-statement</code>.
      </li>

      <li>
        <b>Inside Conditional Loop:</b>  
        <code>loop-less-than</code> → <code>if-equals-to</code> → <code>break</code> → <code>close-statement</code> → <code>close-statement</code>  
        Result: Loop stops when the condition for break is met.
      </li>
    </ul>
                                    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/break.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        <code>break</code> can only be used inside loop blocks.  
        It immediately stops the loop and skips remaining actions within that loop.  
        Execution resumes after the loop's <code>close-statement</code>.
      </span>
    </div>
  </div>
`  ,
  'continue': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">continue</h1>

    <div class="main-section-title">Description</div>
    <p>
      Skips the remaining actions of the current loop iteration and moves to the next iteration.  
      This action is only applicable inside loop blocks such as <code>loop-count</code>, <code>loop-equals</code>, <code>loop-less-than</code>, or similar loops.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">continue</div>
    </div>
    <ul>
      <li><b>Only Part</b> – The action (<b>continue</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Immediately stops the current iteration of the loop.</li>
        <li>Skips all remaining actions inside this iteration.</li>
        <li>Starts the next iteration of the loop, if any remain, until <code>close-statement</code> is reached.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Skipping certain actions in a loop when a condition is met without stopping the entire loop.</li>
      <li>Skipping iterations where an element is not found or a condition is false.</li>
      <li>Efficient looping by avoiding unnecessary actions for some iterations.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Inside Loop Count:</b>  
        <code>loop-count</code> → (actions) → <code>continue</code> → (remaining actions skipped) → <code>close-statement</code>  
        Result: Current iteration stops at <code>continue</code> and the next iteration starts.
      </li>

      <li>
        <b>Conditional Skip:</b>  
        <code>loop-count</code> → <code>if-equals-to</code> → <code>continue</code> → <code>close-statement</code> → <code>close-statement</code>  
        Result: If condition is true, remaining actions in this iteration are skipped and the next iteration begins.
      </li>
    </ul>
                                        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/continue.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        <code>continue</code> can only be used inside loop blocks.  
        It skips the remaining actions of the current iteration but does not exit the loop.  
        Execution resumes with the next iteration until the <code>close-statement</code> is reached.
      </span>
    </div>
  </div>
`  ,
  'close-statement': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">close-statement</h1>

    <div class="main-section-title">Description</div>
    <p>
      Marks the end of a control block.  
      This action is used to close <code>if</code>, <code>else-if</code>, <code>else</code>, and loop blocks.  
      Execution continues normally after this action.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">close-statement</div>
    </div>
    <ul>
      <li><b>Only Part</b> – The action (<b>close-statement</b>) with maroon background.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Ends the currently active conditional or loop block.</li>
        <li>Returns execution flow to the parent or main sequence.</li>
        <li>Determines the next step based on the type of block that was closed.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Closing an <code>if</code> condition block.</li>
      <li>Closing an <code>else-if</code> or <code>else</code> block.</li>
      <li>Closing loop blocks such as <code>for</code>, <code>while</code>, or <code>repeat</code>.</li>
      <li>Ensuring correct execution flow in nested logic.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Conditional Flow:</b><br/>
        <code>if-condition</code> → <code>close-statement</code>
      </li>

      <li>
        <b>Conditional Flow with Else:</b><br/>
        <code>if-condition</code> → <code>close-statement</code> → <code>else</code> → <code>close-statement</code>
      </li>

      <li>
        <b>Loop Flow:</b><br/>
        <code>loop-start</code> → (actions) → <code>close-statement</code>
      </li>

      <li>
        <b>Nested Flow:</b><br/>
        <code>loop-start</code> → <code>if-condition</code> → <code>close-statement</code> → <code>close-statement</code>
      </li>
    </ul>
                                            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/close statement.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every conditional and loop block must end with a <code>close-statement</code>.  
        The <code>close-statement</code> always closes the most recently opened block.  
        Missing or misplaced <code>close-statement</code> actions may cause incorrect execution flow.
      </span>
    </div>
  </div>
`  ,
  'loop-count': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">loop-count</h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a set of actions a specified number of times.  
      This loop runs exactly as many times as the number specified in the value field.  
      The loop block must be closed with a <code>close-statement</code> to indicate the end of the loop.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">loop-count</div>
      <div class="press-key-key">Number of Iterations</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>loop-count</b>) with maroon background.</li>
      <li><b>Second Part</b> – The number of times to execute the loop (blue background). Can be a number or a variable containing a number.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Reads the number of iterations from the value provided.</li>
        <li>Executes all actions inside this block the specified number of times.</li>
        <li>After the loop completes, execution continues after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Repeating actions like clicking a button multiple times.</li>
      <li>Filling forms or data entry in repeated steps.</li>
      <li>Dynamic loops using variables for the iteration count.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>loop-count</code>  
        <b>Value:</b> <code>2</code>
      </li>
      <li>
        Result: The actions inside this loop block will execute exactly 2 times.  
        The loop ends at the <code>close-statement</code>.
      </li>

      <li>
        <b>Using a Variable:</b> <code>loop-count</code> with <code>##iterationCount</code> (where <code>##iterationCount = 3</code>)  
        Result: The block executes 3 times and stops at <code>close-statement</code>.
      </li>
    </ul>
                                                <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/loop count.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>loop-count</code> block must be closed with a <code>close-statement</code>.  
        The value must be a positive number or a variable containing a positive number.  
        Nested loops are allowed by using multiple <code>loop-count</code> blocks, each closed with its own <code>close-statement</code>.
      </span>
    </div>
  </div>
`  ,
  'loop-equals': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">loop-equals</h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a loop only if two values are equal.  
      Both values can be text or numbers.  
      If the values are not equal, the block is skipped.  
      The loop block must be closed with a <code>close-statement</code> to mark the end.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">loop-equals</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>loop-equals</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value (blue background). Can be text, number, or variable.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be text, number, or variable.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first value to the second value.</li>
        <li>If the values are equal, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If the values are not equal, skips this block entirely and continues after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Executing actions only when two values match.</li>
      <li>Performing repeated tasks conditionally based on text or number equality.</li>
      <li>Dynamic loops using variables for the first and second value.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>loop-equals</code>  
        <b>First Value:</b> <code>test</code>  
        <b>Second Value:</b> <code>test</code>
      </li>
      <li>Result: <code>True</code> – The values match, so all actions inside this block execute until <code>close-statement</code>.</li>

      <li>
        <b>Action:</b> <code>loop-equals</code>  
        <b>First Value:</b> <code>test</code>  
        <b>Second Value:</b> <code>test1</code>
      </li>
      <li>Result: <code>False</code> – Values do not match, so the block is skipped.</li>

      <li>
        <b>Using a Variable:</b> <code>loop-equals</code> with <code>##myValue</code> = 123 and second value 123  
        Result: <code>True</code> – Block executes because the values are equal.
      </li>
    </ul>
                                                    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/loop equals.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>loop-equals</code> block must be closed with a <code>close-statement</code>.  
        Both values can be text, number, or variable.  
        True condition executes the block; false condition skips it entirely.
      </span>
    </div>
  </div>
`  ,
  'loop-less-than': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">loop-less-than</h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a loop only if the first value is less than the second value.  
      Both values must be numbers.  
      If the condition is false, the block is skipped.  
      The loop block must be closed with a <code>close-statement</code> to mark the end.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">loop-less-than</div>
      <div class="press-key-key">First Value</div>
      <div class="press-key-key">Second Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>loop-less-than</b>) with maroon background.</li>
      <li><b>Second Part</b> – The first value (blue background). Can be a number or a variable containing a number.</li>
      <li><b>Third Part</b> – The second value to compare (blue background). Can be a number or a variable containing a number.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Compares the first value to the second value numerically.</li>
        <li>If the first value is less than the second value, executes all actions inside this block until a <code>close-statement</code>.</li>
        <li>If the condition is false, skips this block entirely and continues after the <code>close-statement</code>.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Repeating actions only while a number is less than a target value.</li>
      <li>Performing loops based on numeric conditions dynamically using variables.</li>
      <li>Conditional loops to limit iterations.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>loop-less-than</code>  
        <b>First Value:</b> <code>2</code>  
        <b>Second Value:</b> <code>5</code>
      </li>
      <li>Result: <code>True</code> – The first value is less than the second, so the block executes until <code>close-statement</code>.</li>

      <li>
        <b>Action:</b> <code>loop-less-than</code>  
        <b>First Value:</b> <code>7</code>  
        <b>Second Value:</b> <code>5</code>
      </li>
      <li>Result: <code>False</code> – The first value is not less than the second, so the block is skipped.</li>

      <li>
        <b>Using a Variable:</b> <code>loop-less-than</code> with <code>##counter</code> = 3 and second value 10  
        Result: <code>True</code> – Block executes because 3 &lt; 10.
      </li>
    </ul>
                                                        <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/loop equalsloop less than.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Every <code>loop-less-than</code> block must be closed with a <code>close-statement</code>.  
        Both values must be numeric or variables containing numbers.  
        True condition executes the block; false condition skips it entirely.
      </span>
    </div>
  </div>
`  ,
  'comment': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">comment</h1>

    <div class="main-section-title">Description</div>
    <p>
      Adds a comment or note in the automation flow for documentation or readability purposes.  
      Comments are **not executed** and do not affect the workflow.  
      Useful to describe the purpose of the following actions or block.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">comment</div>
      <div class="press-key-key">Comment Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>comment</b>) with maroon background.</li>
      <li><b>Second Part</b> – The comment text (blue background). Can be any text describing the purpose.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Does not perform any operation for this action.</li>
        <li>Displays the comment text in the execution log or editor for reference.</li>
        <li>Helps maintain readable and documented automation flows.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Explaining the purpose of a block of actions, e.g., “This block is for login.”</li>
      <li>Adding notes or reminders for future edits in the automation flow.</li>
      <li>Temporarily marking sections of code without affecting execution.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>comment</code>  
        <b>Value:</b> <code>This block handles the login process</code>
      </li>
      <li>Result: The text appears in the log or editor as a note; no actions are executed.</li>

      <li>
        <b>Action:</b> <code>comment</code>  
        <b>Value:</b> <code>Skipping optional verification step</code>
      </li>
      <li>Result: Helps anyone reading the flow understand why certain steps are skipped or included.</li>
    </ul>
                                                            <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/comment.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        The <code>comment</code> action does not execute any operations and is purely for documentation.  
        Both text and variables can be used to make the comment meaningful.
      </span>
    </div>
  </div>
`};
export const verifyMap: { [key: string]: string } = {
  'verify-element-present': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-present</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified element is present on the currently loaded page.
    This verification is useful for validation steps in tests or automation flows.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-present</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-present).</li>
    <li><b>Second Part</b> – The element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Checks if the element exists on the page.</li>
      <li>Passes or fails the verification based on whether the element is present.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a login button is present before attempting a login</li>
    <li>Ensuring that a specific section or widget exists on a page</li>
    <li>Verifying elements dynamically loaded via JavaScript or AJAX</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-present</code>  
      <b>Element:</b> <code>#loginButton</code>
    </li>
    <li>
      This checks if the element with ID <code>loginButton</code> exists on the page.  
      If present, the step passes; if not, the step fails.
    </li>
  </ul>
  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element present.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks for presence; it does not validate visibility or enablement.
    </span>
  </div>
</div>
`  ,
  'verify-element-not-present': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-not-present</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified element is <strong>not present</strong> on the currently loaded page.
    This is useful for negative validation steps in tests or automation flows.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-not-present</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-not-present).</li>
    <li><b>Second Part</b> – The element to check. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Attempts to locate the element using the provided locator.</li>
      <li>Verifies that the element does <strong>not</strong> exist on the page.</li>
      <li>Passes the verification if the element is absent, and fails if it is found.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Confirming that an error message is not displayed</li>
    <li>Validating that a deprecated element or popup is removed</li>
    <li>Ensuring that certain elements are not loaded in negative test scenarios</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-not-present</code>  
      <b>Element:</b> <code>#oldBanner</code>
    </li>
    <li>
      This checks if the element with ID <code>oldBanner</code> is not present on the page.  
      If absent, the step passes; if it exists, the step fails.
    </li>
  </ul>
    <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element not present.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks for absence; it does not validate visibility or enablement.
    </span>
  </div>
</div>
`  ,
  'verify-element-enabled': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-enabled</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified element on the currently loaded page is <strong>enabled</strong> and can be interacted with.
    This is useful for validating form fields, buttons, or any interactive elements.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-enabled</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-enabled).</li>
    <li><b>Second Part</b> – The element to check. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Checks if the element is enabled (i.e., not disabled and ready for interaction).</li>
      <li>Passes the verification if the element is enabled, and fails if it is disabled or not interactable.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a form’s submit button is enabled before clicking</li>
    <li>Ensuring input fields are editable</li>
    <li>Checking that interactive UI elements are ready for user actions</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-enabled</code>  
      <b>Element:</b> <code>#submitButton</code>
    </li>
    <li>
      This checks if the element with ID <code>submitButton</code> is enabled.  
      If enabled, the step passes; if disabled, the step fails.
    </li>
  </ul>
     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element enabled.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks for enablement; it does not check visibility or presence.
    </span>
  </div>
</div>
`  ,
  'verify-element-checked': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-checked</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified checkbox or radio button element on the currently loaded page is <strong>checked</strong>.
    This is useful for validating selections in forms, settings, or any UI elements that can be toggled.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-checked</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-checked).</li>
    <li><b>Second Part</b> – The checkbox or radio button element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the checkbox or radio button using the provided locator.</li>
      <li>Checks whether the element is currently checked/selected.</li>
      <li>Passes the verification if the element is checked, and fails if it is unchecked.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a terms-and-conditions checkbox is selected</li>
    <li>Ensuring default options in a form are checked</li>
    <li>Verifying radio button selections in settings or configuration pages</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-checked</code>  
      <b>Element:</b> <code>#termsCheckbox</code>
    </li>
    <li>
      This checks if the checkbox with ID <code>termsCheckbox</code> is checked.  
      If checked, the step passes; if unchecked, the step fails.
    </li>
  </ul>
       <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element checked.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks the checked state; it does not validate visibility, enablement, or presence.
    </span>
  </div>
</div>
`  , 'verify-element-unchecked': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-unchecked</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified checkbox or radio button element on the currently loaded page is <strong>unchecked</strong>.
    This is useful for validating that options are not selected by default or have been successfully deselected.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-unchecked</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-unchecked).</li>
    <li><b>Second Part</b> – The checkbox or radio button element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the checkbox or radio button using the provided locator.</li>
      <li>Checks whether the element is currently unchecked/not selected.</li>
      <li>Passes the verification if the element is unchecked, and fails if it is checked.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a terms-and-conditions checkbox is not selected by default</li>
    <li>Ensuring an option has been successfully deselected</li>
    <li>Verifying that a radio button is not selected</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-unchecked</code>  
      <b>Element:</b> <code>#termsCheckbox</code>
    </li>
    <li>
      This checks if the checkbox with ID <code>termsCheckbox</code> is unchecked.  
      If unchecked, the step passes; if checked, the step fails.
    </li>
  </ul>
         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element unchecked.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks the checked state; it does not validate visibility, enablement, or presence.
    </span>
  </div>
</div>
`,
  'verify-element-editable': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-editable</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified input field or text area on the currently loaded page is <strong>editable</strong>.
    This is useful for validating form fields or any element where user input is expected.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-editable</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-editable).</li>
    <li><b>Second Part</b> – The input or text area element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Checks whether the element is editable (i.e., not disabled or read-only).</li>
      <li>Passes the verification if the element is editable, and fails if it is not.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a form input field can accept text</li>
    <li>Ensuring text areas or comment boxes are writable</li>
    <li>Checking dynamic fields for enablement before user interaction</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-editable</code>  
      <b>Element:</b> <code>#usernameField</code>
    </li>
    <li>
      This checks if the input field with ID <code>usernameField</code> is editable.  
      If editable, the step passes; if read-only or disabled, the step fails.
    </li>
  </ul>
           <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element editable.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks editability; it does not validate visibility, presence, or value.
    </span>
  </div>
</div>
`  ,
  'verify-element-not-editable': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-element-not-editable</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified input field or text area on the currently loaded page is <strong>not editable</strong>.
    This is useful for validating read-only fields or disabled form elements.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-element-not-editable</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-element-not-editable).</li>
    <li><b>Second Part</b> – The input or text area element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Checks whether the element is not editable (i.e., disabled or read-only).</li>
      <li>Passes the verification if the element is not editable, and fails if it is editable.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a form field is read-only</li>
    <li>Ensuring certain fields cannot be modified by the user</li>
    <li>Checking system-generated or disabled fields before form submission</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-element-not-editable</code>  
      <b>Element:</b> <code>#userIdField</code>
    </li>
    <li>
      This checks if the input field with ID <code>userIdField</code> is not editable.  
      If it is read-only or disabled, the step passes; if editable, the step fails.
    </li>
  </ul>
             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify element not editable.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable verification.  
      This action only checks non-editability; it does not validate visibility, presence, or value.
    </span>
  </div>
</div>
`  ,
  'verify-selected-option': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-selected-option</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether a specified dropdown (HTML &lt;select&gt;) element has a particular option selected.
    This is useful for validating default selections or choices made by the user.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-selected-option</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-selected-option).</li>
    <li><b>Second Part</b> – The dropdown element to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The option value or visible text you expect to be selected.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Finds the &lt;select&gt; element using the provided locator.</li>
      <li>Checks the currently selected option.</li>
      <li>Verifies that the selected option matches the expected value provided.</li>
      <li>Passes the verification if it matches, fails if it does not.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating default country or state selections in forms</li>
    <li>Ensuring a previously selected option persists on page reload</li>
    <li>Verifying user-selected options in dropdown menus</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-selected-option</code>  
      <b>Element:</b> <code>#countryDropdown</code>  
      <b>Value:</b> <code>United States</code>
    </li>
    <li>
      This checks if the dropdown with ID <code>countryDropdown</code> currently has <code>United States</code> selected.  
      If yes, the step passes; if another option is selected, it fails.
    </li>
  </ul>
               <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify select option.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The value must match exactly, including spaces and capitalization.  
      Only works for standard HTML &lt;select&gt; dropdowns.
    </span>
  </div>
</div>
`  ,
  'verify-text': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-text</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether the text of a specified element matches the expected value exactly.  
    This is useful for validating labels, messages, headings, or any visible text on the page.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-text</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-text).</li>
    <li><b>Second Part</b> – The element whose text you want to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The expected text value.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Retrieves the visible text of the element.</li>
      <li>Compares it exactly with the expected value provided.</li>
      <li>Passes the verification if the text matches exactly, fails if it does not.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating headings, labels, or messages on a page</li>
    <li>Checking error or success messages in forms</li>
    <li>Verifying static or dynamic text content</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-text</code>  
      <b>Element:</b> <code>#welcomeMessage</code>  
      <b>Value:</b> <code>Welcome, John!</code>
    </li>
    <li>
      This checks if the element with ID <code>welcomeMessage</code> contains the exact text <code>Welcome, John!</code>.  
      If yes, the step passes; if it contains different text, it fails.
    </li>
  </ul>
  <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The comparison is case-sensitive and must match exactly, including spaces and punctuation.
    </span>
  </div>
</div>
`  ,
  'verify-text-contains': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-text-contains</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether the text of a specified element contains a given substring.  
    This is useful when you want to check for partial text, dynamic content, or messages that may change slightly.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-text-contains</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-text-contains).</li>
    <li><b>Second Part</b> – The element whose text you want to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The expected substring value.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Retrieves the visible text of the element.</li>
      <li>Checks if the text contains the expected substring.</li>
      <li>Passes the verification if the substring is found, fails if it is not.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating that a success message contains certain keywords</li>
    <li>Checking partial headings or dynamic content</li>
    <li>Verifying that a specific word or phrase appears in a paragraph or label</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-text-contains</code>  
      <b>Element:</b> <code>#statusMessage</code>  
      <b>Value:</b> <code>successfully</code>
    </li>
    <li>
      This checks if the element with ID <code>statusMessage</code> contains the word <code>successfully</code>.  
      If yes, the step passes; if not, it fails.
    </li>
  </ul>

<div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify text contains.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The search is case-sensitive. Ensure the substring matches exactly, including spaces.
    </span>
  </div>
</div>
`  ,
  'verify-text-ignore-case': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">verify-text-ignore-case</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies whether the text of a specified element matches the expected value, <strong>ignoring case differences</strong>.  
    This is useful when text capitalization may vary but the content itself should match.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-text-ignore-case</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (verify-text-ignore-case).</li>
    <li><b>Second Part</b> – The element whose text you want to verify. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The expected text value (case differences will be ignored).</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Retrieves the visible text of the element.</li>
      <li>Compares it with the expected value, ignoring case differences.</li>
      <li>Passes the verification if the texts match ignoring case, fails if they do not.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating headings, labels, or messages where capitalization may vary</li>
    <li>Checking dynamic content while ignoring case differences</li>
    <li>Verifying user interface text consistency without strict case sensitivity</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-text-ignore-case</code>  
      <b>Element:</b> <code>#welcomeMessage</code>  
      <b>Value:</b> <code>welcome, john!</code>
    </li>
    <li>
      This checks if the element with ID <code>welcomeMessage</code> contains the text <code>welcome, john!</code>, ignoring case.  
      If yes, the step passes; if not, it fails.
    </li>
  </ul>
  

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Spaces, punctuation, and characters must match exactly, but letter case is ignored.
    </span>
  </div>
</div>
`  ,
  'assert-text': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">assert-text</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts that the text of a specified element matches the expected value exactly.  
    Unlike verification actions, an assertion typically stops the test immediately if it fails.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-text</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (assert-text).</li>
    <li><b>Second Part</b> – The element whose text you want to assert. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The expected text value.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Retrieves the visible text of the element.</li>
      <li>Compares it exactly with the expected value provided.</li>
      <li>If the text matches, the test continues; if it does not, the test stops and reports a failure.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating critical labels, headings, or messages that must be exact</li>
    <li>Ensuring error or success messages match expected content before proceeding</li>
    <li>Checking important UI text that should not vary</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-text</code>  
      <b>Element:</b> <code>#welcomeMessage</code>  
      <b>Value:</b> <code>Welcome, John!</code>
    </li>
    <li>
      This asserts that the element with ID <code>welcomeMessage</code> contains the exact text <code>Welcome, John!</code>.  
      If it does not, the test will fail immediately.
    </li>
  </ul>
                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/asserted text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The comparison is case-sensitive and must match exactly, including spaces and punctuation.  
      Assertions are stricter than verification actions because a failure halts the test flow.
    </span>
  </div>
</div>
`  ,
  'assert-value': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">assert-value</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts that the value of a specified input field, textarea, or dropdown matches the expected value exactly.  
    Unlike verification actions, an assertion typically stops the test immediately if it fails.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-value</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">value</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (assert-value).</li>
    <li><b>Second Part</b> – The element whose value you want to assert. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
    <li><b>Third Part</b> – The expected value of the element.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Retrieves the value of the element (from the <code>value</code> attribute).</li>
      <li>Compares it exactly with the expected value provided.</li>
      <li>If the value matches, the test continues; if it does not, the test stops and reports a failure.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating input fields, text areas, or dropdowns contain expected values</li>
    <li>Ensuring form fields are pre-filled with correct default data</li>
    <li>Checking dynamic form entries or selections before proceeding</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-value</code>  
      <b>Element:</b> <code>#usernameField</code>  
      <b>Value:</b> <code>john_doe</code>
    </li>
    <li>
      This asserts that the input field with ID <code>usernameField</code> has the exact value <code>john_doe</code>.  
      If it does not, the test will fail immediately.
    </li>
  </ul>
  

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The comparison is case-sensitive and must match exactly, including spaces and special characters.  
      Assertions are stricter than verification actions because a failure halts the test flow.
    </span>
  </div>
</div>
`, 'assert-element-checked': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">assert-element-checked</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts whether a specified checkbox or radio button element on the currently loaded page is <strong>checked</strong>.
    Unlike verification, an assertion will immediately fail the test if the condition is not met.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-element-checked</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (assert-element-checked).</li>
    <li><b>Second Part</b> – The checkbox or radio button element to assert. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the checkbox or radio button using the provided locator.</li>
      <li>Checks whether the element is currently checked/selected.</li>
      <li>Immediately fails the test if the element is unchecked; passes if it is checked.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Ensuring that required checkboxes, like terms-and-conditions, are selected before proceeding</li>
    <li>Validating default selections in forms or settings pages</li>
    <li>Verifying that a specific radio button has been selected in a workflow</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-element-checked</code>  
      <b>Element:</b> <code>#termsCheckbox</code>
    </li>
    <li>
      This asserts that the checkbox with ID <code>termsCheckbox</code> is checked.  
      If unchecked, the test fails immediately.
    </li>
  </ul>
                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/assert element checked.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable assertion.  
      This action only checks the checked state; it does not validate visibility, enablement, or presence.
    </span>
  </div>
</div>
`,
  'assert-element-unchecked': `
<div>
  <h1 style="font-size: 2em; font-weight: 700;">assert-element-unchecked</h1>

  <div class="main-section-title">Description</div>
  <p>
    Asserts whether a specified checkbox or radio button element on the currently loaded page is <strong>unchecked</strong>.
    Unlike verification, an assertion will immediately fail the test if the condition is not met.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">assert-element-unchecked</div>
    <div class="press-key-element">element</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (assert-element-unchecked).</li>
    <li><b>Second Part</b> – The checkbox or radio button element to assert. Use a proper locator such as ID, name, CSS selector, or XPath.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the checkbox or radio button using the provided locator.</li>
      <li>Checks whether the element is currently unchecked/not selected.</li>
      <li>Immediately fails the test if the element is checked; passes if it is unchecked.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Ensuring that optional checkboxes are not selected by default</li>
    <li>Validating that deselection actions in forms have been applied correctly</li>
    <li>Verifying that a specific radio button is not selected</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>assert-element-unchecked</code>  
      <b>Element:</b> <code>#newsletterCheckbox</code>
    </li>
    <li>
      This asserts that the checkbox with ID <code>#newsletterCheckbox</code> is unchecked.  
      If checked, the test fails immediately.
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/assert element unchecked.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Use proper locators for reliable assertion.  
      This action only checks the checked state; it does not validate visibility, enablement, or presence.
    </span>
  </div>
</div>
`
};
export const dataMap: { [key: string]: string } = {
  'get-text': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-text</h1>

  <div class="main-section-title">Description</div>
  <p>
    Retrieves the visible text from a specified page element, verifies that it contains
    the expected value, and stores the retrieved text in a variable for later use.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">get-text</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">##variable</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>get-text</b>) with maroon background.</li>
    <li><b>Second Part</b> – The element from which text is retrieved (gray background). Use a valid locator such as ID, name, or CSS selector.</li>
    <li><b>Third Part</b> – The variable name prefixed with <code>##</code> (blue background) where the retrieved text will be stored.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Extracts the visible text from the element.</li>
      <li>Verifies the text contains the expected condition.</li>
      <li>Stores the retrieved text into the specified variable.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Capturing dynamic messages for later validation</li>
    <li>Storing order numbers, usernames, or status text</li>
    <li>Reusing UI text in subsequent steps</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>get-text</code>  
      <b>Element:</b> <code>#orderStatus</code>  
      <b>Store As:</b> <code>##orderStatusText</code>
    </li>
    <li>
      This retrieves the text from <code>#orderStatus</code>, verifies it contains the expected content,
      and stores it in <code>##orderStatusText</code> for later use.
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Variables must be prefixed with <code>##</code>.  
      The stored value can be reused in subsequent actions by referencing the same variable name.
    </span>
  </div>
</div>
`,
  'get-value': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-value</h1>

  <div class="main-section-title">Description</div>
  <p>
    Retrieves the current value of an input or form element and stores it in a variable
    for later use. This action is commonly used to capture user-entered or system-generated values.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">get-value</div>
    <div class="press-key-element">element</div>
    <div class="press-key-key">##variable</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>get-value</b>) with maroon background.</li>
    <li><b>Second Part</b> – The element whose value will be retrieved (gray background). Typically an <code>&lt;input&gt;</code>, <code>&lt;textarea&gt;</code>, or form field.</li>
    <li><b>Third Part</b> – The variable name prefixed with <code>##</code> (blue background) where the retrieved value will be stored.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Locates the element using the provided locator.</li>
      <li>Reads the element’s <code>value</code> attribute.</li>
      <li>Stores the retrieved value in the specified variable.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Capturing text entered into input fields</li>
    <li>Storing dynamically generated form values</li>
    <li>Reusing input values in later steps</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>get-value</code>  
      <b>Element:</b> <code>#emailInput</code>  
      <b>Store As:</b> <code>##userEmail</code>
    </li>
    <li>
      This retrieves the value from <code>#emailInput</code> and stores it in <code>##userEmail</code>
      for later use.
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get value.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Variables must be prefixed with <code>##</code>.  
      This action works with elements that support the <code>value</code> attribute.
    </span>
  </div>
</div>
`,
  'get-positioned-word': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-positioned-word</h1>

  <div class="main-section-title">Description</div>
  <p>
    Extracts a word from a paragraph stored in a variable based on its position (1-based index)
    and stores that word in a new variable for later use.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">get-positioned-word</div>
    <div class="press-key-key">##paragraphVariable</div>
    <div class="press-key-key">Word Position</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>get-positioned-word</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable containing the paragraph text (blue background), e.g., <code>##myParagraph</code>.</li>
    <li><b>Third Part</b> – The word position to extract (blue background). This is a 1-based index; e.g., <code>2</code> extracts the second word.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Reads the text from the variable provided in the second part.</li>
      <li>Splits the text into words using spaces as separators.</li>
      <li>Extracts the word at the position specified in the third part.</li>
      <li>Stores the extracted word in a **new variable** for later use (system-defined or user-specified depending on your setup).</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Extracting the nth word from a sentence or paragraph</li>
    <li>Parsing key words from user input or messages</li>
    <li>Working with dynamic content where specific words need to be reused</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>get-positioned-word</code>  
      <b>Paragraph Variable:</b> <code>##myParagraph</code>  
      <b>Word Position:</b> <code>2</code>
    </li>
    <li>
      If <code>##myParagraph</code> contains <code>"Hello world from ChatGPT"</code>,  
      the extracted word will be <code>"world"</code>, stored in a new variable (e.g., <code>##word</code>).
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get positioned word.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Both the second and third parts are **values** (blue background).  
      Word positions are 1-based. If the position exceeds the number of words, the action returns an empty string or triggers an error depending on system configuration.  
      Words are separated by spaces; punctuation may be considered part of the word.
    </span>
  </div>
</div>
`,
  'get-text-search-count': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-text-search-count</h1>

  <div class="main-section-title">Description</div>
  <p>
    Counts the number of occurrences of a specified word within an already opened page
    and stores the count in a variable for later use. The search is case-sensitive.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">get-text-search-count</div>
    <div class="press-key-key">##pageContentVariable</div>
    <div class="press-key-key">Word To Search</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>get-text-search-count</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable containing the page text (blue background), e.g., <code>##myValue</code>.</li>
    <li><b>Third Part</b> – The word to search for (blue background), e.g., <code>web</code>. The search is case-sensitive.</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Reads the content from the variable containing the opened page text.</li>
      <li>Counts the number of occurrences of the specified word (case-sensitive).</li>
      <li>Stores the resulting count in a variable for later use.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Counting how many times a keyword appears on an opened webpage</li>
    <li>Validating dynamic text content after opening a URL</li>
    <li>Checking keyword frequency for testing or scraping purposes</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>get-text-search-count</code>  
      <b>Variable:</b> <code>##myValue</code>  
      <b>Word:</b> <code>web</code>
    </li>
    <li>
      Workflow Example:
      <ol>
        <li>Use <code>open-url</code> to open <code>https://example.com</code>.</li>
        <li>Store the page content into <code>##myValue</code>.</li>
        <li>Use <code>get-text-search-count</code> with <code>##myValue</code> and the word <code>web</code>.</li>
        <li>The system counts how many times <code>web</code> occurs (case-sensitive) and stores the result in a variable (e.g., <code>##wordCount</code>).</li>
      </ol>
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get text search count.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      Both the second and third parts are **values** (blue background).  
      The search is case-sensitive.  
      The resulting count is stored in a variable for reuse in subsequent steps.
    </span>
  </div>
</div>
`,
  'get-link-from-element': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-link-from-element</h1>

    <div class="main-section-title">Description</div>
    <p>
      Retrieves the URL (link) from a specified element (like an anchor tag) and stores it into a variable.  
      Useful for validating links, logging URLs, or using them in subsequent automation steps.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">get-link-from-element</div>
      <div class="press-key-element">Element</div>
      <div class="press-key-key">##variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>get-link-from-element</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element (gray background) containing the link. Can be located using ID, CSS selector, XPath, or other locators.</li>
      <li><b>Third Part</b> – The variable (blue background) where the link will be stored. Must start with <code>##</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Reads the <code>href</code> attribute from the element.</li>
        <li>Stores the link in the specified variable.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating navigation links.</li>
      <li>Extracting URLs for later use.</li>
      <li>Logging links for debugging or reporting.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>get-link-from-element</code>  
        <b>Element:</b> <code>#homeLink</code>  
        <b>Variable:</b> <code>##homeURL</code>
      </li>
      <li>Result: The variable <code>##homeURL</code> now contains "https://example.com/home".</li>

      <li>
        <b>Action:</b> <code>get-link-from-element</code>  
        <b>Element:</b> <code>//a[@id='contact']</code>  
        <b>Variable:</b> <code>##contactURL</code>
      </li>
      <li>Result: The variable <code>##contactURL</code> now contains "https://example.com/contact".</li>
    </ul>
    

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element exists and has an <code>href</code> attribute.  
        The retrieved URL is stored in the specified variable and can be used in subsequent actions.
      </span>
    </div>
  </div>
`, 'get-current-time': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">get-current-time</h1>

  <div class="main-section-title">Description</div>
  <p>
    Retrieves the current date and time based on the specified format and optional timezone,
    and stores the result in a variable for later use.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">get-current-time</div>
    <div class="press-key-key">##resultVariable</div>
    <div class="press-key-key">&lt;date-time-pattern&gt;|&lt;zone-id&gt;</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>get-current-time</b>) with maroon background.</li>
    <li><b>Second Part</b> – The variable (blue background) where the current date/time will be stored. Example: <code>##currentTime</code>.</li>
    <li><b>Third Part</b> – The date-time pattern, optionally followed by a timezone using the format: <code>&lt;pattern&gt;|&lt;zone-id&gt;</code> (blue background).</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Reads the provided date-time pattern.</li>
      <li>If a timezone is provided, uses that timezone; otherwise uses the system timezone.</li>
      <li>Generates the current date and time.</li>
      <li>Formats it according to the specified pattern.</li>
      <li>Stores the formatted result in the given variable.</li>
    </ul>
  </p>

  <div class="main-section-title">Pattern Rules</div>
  <ul>
    <li><b>Pattern only</b> → Uses pattern with system timezone.</li>
    <li><b>Pattern|Timezone</b> → Uses pattern with provided timezone.</li>
  </ul>

  <div class="main-section-title">Supported Pattern Examples</div>
  <ul>
    <li><code>yyyy-MM-dd</code></li>
    <li><code>dd/MM/yyyy</code></li>
    <li><code>yyyy-MM-dd HH:mm:ss</code></li>
    <li><code>MM-dd-yyyy hh:mm a</code></li>
    <li><code>EEEE, MMM dd yyyy</code></li>
    <li><code>yyyyMMddHHmmss</code></li>
  </ul>

  <div class="main-section-title">Examples with Timezone</div>
  <ul>
    <li><code>yyyy-MM-dd|UTC</code></li>
    <li><code>yyyy-MM-dd HH:mm:ss|Asia/Kolkata</code></li>
    <li><code>dd/MM/yyyy HH:mm|Europe/London</code></li>
    <li><code>MM-dd-yyyy hh:mm a|America/New_York</code></li>
    <li><code>yyyyMMddHHmmss|Australia/Sydney</code></li>
  </ul>

  <div class="main-section-title">Example Usage</div>
  <ul>
    <li>
      <b>Action:</b> <code>get-current-time</code>  
      <b>Variable:</b> <code>##currentDate</code>  
      <b>Pattern:</b> <code>yyyy-MM-dd</code>
    </li>
    <li>
      Result: Stores current date in system timezone (e.g., <code>2026-02-27</code>).
    </li>

    <li>
      <b>Action:</b> <code>get-current-time</code>  
      <b>Variable:</b> <code>##indiaTime</code>  
      <b>Pattern:</b> <code>yyyy-MM-dd HH:mm:ss|Asia/Kolkata</code>
    </li>
    <li>
      Result: Stores current date and time in Asia/Kolkata timezone.
    </li>
  </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/get current time.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The second and third parts are values (blue background).  
      The timezone must be a valid Zone ID (e.g., UTC, Asia/Kolkata, Europe/London).  
      If no timezone is provided, the system timezone will be used by default.
    </span>
  </div>
</div>
`,
  'show-text-search-count': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">show-text-search-count</h1>

    <div class="main-section-title">Description</div>
    <p>
      Counts the number of times a specified text appears on a page or in a variable, and stores the count into another variable.  
      Useful for validations, testing content frequency, or reporting occurrences.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">show-text-search-count</div>
      <div class="press-key-key">Text / Variable</div>
      <div class="press-key-key">Count Variable</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>show-text-search-count</b>) with maroon background.</li>
      <li><b>Second Part</b> – The text to search or a variable containing the text (gray background). For example: <code>##pageContent</code> or direct text <code>"Error"</code>.</li>
      <li><b>Third Part</b> – The variable where the count will be stored (blue background). Must start with <code>##</code>.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Reads the text or variable provided.</li>
        <li>Searches for all occurrences of the specified text.</li>
        <li>Counts how many times it appears.</li>
        <li>Stores the count into the specified variable.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Counting occurrences of an error message on a page.</li>
      <li>Validating how many times a keyword appears in a paragraph or API response.</li>
      <li>Tracking repeated elements in test automation reports.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>show-text-search-count</code>  
        <b>Text / Variable:</b> <code>##pageContent</code>  
        <b>Count Variable:</b> <code>##errorCount</code>
      </li>
      <li>Result: The variable <code>##errorCount</code> now contains the number of times the text appears in <code>##pageContent</code>.</li>

      <li>
        <b>Action:</b> <code>show-text-search-count</code>  
        <b>Text / Variable:</b> <code>"Login Failed"</code>  
        <b>Count Variable:</b> <code>##loginFailCount</code>
      </li>
      <li>Result: The variable <code>##loginFailCount</code> now contains the number of times "Login Failed" appears on the page.</li>
    </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/show text search count.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the text or variable exists. The search is case-sensitive unless the system automatically ignores case.  
        The count is stored in the specified variable for further actions or validations.
      </span>
    </div>
  </div>
`


};
export const alertsMap: { [key: string]: string } = {
  'accept-alert': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">accept-alert</h1>

    <div class="main-section-title">Description</div>
    <p>
      Accepts a browser alert, confirmation, or prompt dialog that is currently open.  
      This action simulates clicking the "OK" button on the alert.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">accept-alert</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>accept-alert</b>) with maroon background (#a86b63).</li>
      <li><b>Note:</b> This action does not require any element or value sections.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Detects if there is a browser alert, confirmation, or prompt active.</li>
        <li>Clicks the "OK" button to accept the alert.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Accepting a JavaScript alert that pops up on form submission</li>
      <li>Handling confirmation dialogs automatically during automation</li>
    </ul>
                   <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/accept alert.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure an alert is present when executing this action.  
        If no alert is active, the action may throw an error.
      </span>
    </div>
  </div>
`,
  'dismiss-alert': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">dismiss-alert</h1>

    <div class="main-section-title">Description</div>
    <p>
      Dismisses a browser alert, confirmation, or prompt dialog that is currently open.  
      This action simulates clicking the "Cancel" button on the alert.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">dismiss-alert</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>dismiss-alert</b>) with maroon background (#a86b63).</li>
      <li><b>Note:</b> This action does not require any element or value sections.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Detects if there is a browser alert, confirmation, or prompt active.</li>
        <li>Clicks the "Cancel" button to dismiss the alert.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Dismissing a JavaScript confirmation dialog automatically</li>
      <li>Handling unwanted alerts during automation</li>
    </ul>
                       <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/dismiss alert.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure an alert is present when executing this action.  
        If no alert is active, the action may throw an error.
      </span>
    </div>
  </div>
`  ,
  'type-alert-message': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">type-alert-message</h1>

    <div class="main-section-title">Description</div>
    <p>
      Sends text to a browser prompt alert that is currently open.  
      This action is only applicable to JavaScript prompt dialogs (<code>prompt()</code>) and allows you to enter a custom message.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">type-alert-message</div>
      <div class="press-key-key">Text to type</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>type-alert-message</b>) with maroon background (#a86b63).</li>
      <li><b>Second Part</b> – The value section (blue background) where you type the message to send to the prompt alert.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Detects if there is a JavaScript prompt alert active.</li>
        <li>Enters the text provided in the value section into the prompt input.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Entering a username or ID in a JavaScript prompt</li>
      <li>Testing prompt dialogs automatically</li>
      <li>Providing dynamic input during automation flows</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>type-alert-message</code>  
        <b>Value:</b> <code>TestUser123</code>
      </li>
      <li>
        This will type <code>TestUser123</code> into an active prompt alert.
      </li>
    </ul>
                           <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/type alert message.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure a prompt alert is present when executing this action.  
        After typing, you can follow it with <code>accept-alert</code> to submit the input.
      </span>
    </div>
  </div>
`  ,
  'verify-text-in-alert': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">verify-text-in-alert</h1>

    <div class="main-section-title">Description</div>
    <p>
      Verifies that the text displayed in a browser alert, confirmation, or prompt matches the expected value.  
      This action is useful for validating messages shown by JavaScript alerts during automation.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">verify-text-in-alert</div>
      <div class="press-key-key">Expected Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>verify-text-in-alert</b>) with maroon background (#a86b63).</li>
      <li><b>Second Part</b> – The value section (blue background) where you provide the text to verify against the alert content.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Detects if a browser alert, confirmation, or prompt is currently active.</li>
        <li>Compares the alert text with the expected text provided in the value section.</li>
        <li>Passes or fails the verification based on whether the text matches exactly.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Verifying success or error messages in alerts</li>
      <li>Validating confirmation dialogs before proceeding</li>
      <li>Testing dynamic messages in JavaScript alerts</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>verify-text-in-alert</code>  
        <b>Value:</b> <code>Are you sure you want to delete this item?</code>
      </li>
      <li>
        This will check if the alert currently displayed has the exact text <code>Are you sure you want to delete this item?</code>.
      </li>
    </ul>
                               <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify text in alert.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure an alert is present when executing this action.  
        The verification is case-sensitive and must match the alert text exactly.
      </span>
    </div>
  </div>
`  ,
  'verify-text-contains-in-alert': `
<div>
  <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">verify-text-contains-in-alert</h1>

  <div class="main-section-title">Description</div>
  <p>
    Verifies that the text displayed in a browser alert dialog contains a specified value.
    This action is used for validation and does not interact with page elements.
  </p>

  <div class="main-section-title">Structure</div>
  <div class="press-key-structure">
    <div class="press-key-action">verify-text-contains-in-alert</div>
    <div class="press-key-key">Expected Text</div>
  </div>
  <ul>
    <li><b>First Part</b> – The action (<b>verify-text-contains-in-alert</b>) with maroon background (#a86b63).</li>
    <li><b>Second Part</b> – The expected text to verify within the alert (blue background).</li>
  </ul>

  <div class="main-section-title">How It Works</div>
  <p>
    When executed, the system:
    <ul>
      <li>Waits for a browser alert to appear.</li>
      <li>Reads the text displayed in the alert.</li>
      <li>Checks whether the alert text contains the expected value.</li>
    </ul>
  </p>

  <div class="main-section-title">Use Case</div>
  <ul>
    <li>Validating error messages shown in alerts</li>
    <li>Verifying confirmation or warning alerts</li>
    <li>Ensuring correct alert messaging during form submission</li>
  </ul>

  <div class="main-section-title">Example</div>
  <ul>
    <li>
      <b>Action:</b> <code>verify-text-contains-in-alert</code>  
      <b>Value:</b> <code>successfully saved</code>
    </li>
    <li>
      This verifies that the alert text contains <code>successfully saved</code>.
    </li>
  </ul>
                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify text contains in alert.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

  <div class="blue-note">
    <span class="icon">&#9432;</span>
    <strong>Note:</strong>
    <span>
      The comparison is case-sensitive unless otherwise configured.  
      This action fails if no alert is present or if the text does not contain the expected value.
    </span>
  </div>
</div>
`  ,
  'assert-text-in-alert': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">assert-text-in-alert</h1>

    <div class="main-section-title">Description</div>
    <p>
      Asserts that the text displayed in a browser alert, confirmation, or prompt matches the expected text exactly.  
      If the alert text does not match, the assertion will fail, causing the test or automation to stop or report an error.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">assert-text-in-alert</div>
      <div class="press-key-key">Expected Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>assert-text-in-alert</b>) with maroon background (#a86b63).</li>
      <li><b>Second Part</b> – The value section (blue background) where you provide the exact text to assert in the alert.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Detects if a browser alert, confirmation, or prompt is currently active.</li>
        <li>Compares the alert text with the expected text provided in the value section.</li>
        <li>Passes the assertion if the text matches exactly; otherwise, it fails and logs an error.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating critical alert messages before proceeding with automation</li>
      <li>Ensuring exact confirmation messages are shown to users</li>
      <li>Testing alerts where precise text verification is required</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>assert-text-in-alert</code>  
        <b>Value:</b> <code>Are you sure you want to delete this item?</code>
      </li>
      <li>
        This will assert that the currently displayed alert has exactly the text <code>Are you sure you want to delete this item?</code>.  
        If not, the test will fail.
      </li>
    </ul>
                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/assert text in alert.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure an alert is present when executing this action.  
        The assertion is case-sensitive and requires an exact match.
      </span>
    </div>
  </div>
`
};
export const draopDownMap: { [key: string]: string } = {
  'dropdown-select': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">dropdown-select</h1>

    <div class="main-section-title">Description</div>
    <p>
      Selects an option from a custom dropdown that is <b>not created using the HTML &lt;select&gt; tag</b>.  
      Useful for modern web applications where dropdowns are built using divs, spans, or other HTML elements.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">dropdown-select</div>
      <div class="press-key-element">Dropdown Element</div>
      <div class="press-key-key">Visible Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>dropdown-select</b>) with maroon background.</li>
      <li><b>Second Part</b> – The element representing the dropdown (gray background). Can be located using ID, CSS selector, XPath, or other locator.</li>
      <li><b>Third Part</b> – The visible text of the option to select (blue background). Must exactly match the text displayed in the dropdown.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the dropdown element based on the locator provided.</li>
        <li>Expands or clicks the dropdown to show the options.</li>
        <li>Selects the option whose visible text matches the value provided.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Selecting a country or city from a styled dropdown in modern web apps.</li>
      <li>Choosing a category in search filters or e-commerce filters.</li>
      <li>Any dropdown built with divs, spans, or custom HTML instead of the &lt;select&gt; tag.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>dropdown-select</code>  
        <b>Element:</b> <code>#customCountryDropdown</code>  
        <b>Value:</b> <code>Canada</code>
      </li>
      <li>
        This will open the custom dropdown with ID <code>customCountryDropdown</code> and select the option with visible text <code>Canada</code>.
      </li>

      <li>
        <b>Action:</b> <code>dropdown-select</code>  
        <b>Element:</b> <code>//div[@class='category-dropdown']</code>  
        <b>Value:</b> <code>Electronics</code>
      </li>
      <li>
        This will select the option <code>Electronics</code> from the custom category dropdown.
      </li>
    </ul>
                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/dropdown select.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the dropdown is visible and enabled.  
        The visible text must match exactly, including spaces and capitalization.  
        This action is only for dropdowns not built with the HTML <code>&lt;select&gt;</code> tag.
      </span>
    </div>
  </div>
`,
  'select-box-by-visible-text': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">select-box-by-visible-text</h1>

    <div class="main-section-title">Description</div>
    <p>
      Selects an option from a dropdown menu (created using the HTML <code>&lt;select&gt;</code> tag) based on the visible text.  
      This action is only applicable to standard HTML select dropdowns.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">select-box-by-visible-text</div>
      <div class="press-key-element">element</div>
      <div class="press-key-key">Visible Text</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>select-box-by-visible-text</b>) with maroon background.</li>
      <li><b>Second Part</b> – The dropdown element to select from (gray background). Use a proper locator like ID, name, or CSS selector.</li>
      <li><b>Third Part</b> – The visible text of the option you want to select (blue background). It must exactly match the option’s displayed text.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the <code>&lt;select&gt;</code> element based on the element locator provided.</li>
        <li>Searches all options within the select tag.</li>
        <li>Selects the option whose visible text matches the value provided.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Choosing a country from a country dropdown</li>
      <li>Selecting a state or city in a form</li>
      <li>Any dropdown created using the HTML <code>&lt;select&gt;</code> tag</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>select-box-by-visible-text</code>  
        <b>Element:</b> <code>#countryDropdown</code>  
        <b>Value:</b> <code>United States</code>
      </li>
      <li>
        This will select the option with visible text <code>United States</code> from the dropdown with ID <code>countryDropdown</code>.
      </li>
    </ul>
                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/select box by visible text.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action only works for dropdowns created using the HTML <code>&lt;select&gt;</code> tag.  
        Ensure the visible text matches exactly including spaces and capitalization.
      </span>
    </div>
  </div>
`,
  'select-box-by-value': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">select-box-by-value</h1>

    <div class="main-section-title">Description</div>
    <p>
      Selects an option from a dropdown menu (created using the HTML <code>&lt;select&gt;</code> tag) based on the <code>value</code> attribute of the option.  
      This action is only applicable to standard HTML select dropdowns.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">select-box-by-value</div>
      <div class="press-key-element">element</div>
      <div class="press-key-key">Option Value</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>select-box-by-value</b>) with maroon background.</li>
      <li><b>Second Part</b> – The dropdown element to select from (gray background). Use a proper locator like ID, name, or CSS selector.</li>
      <li><b>Third Part</b> – The <code>value</code> attribute of the option you want to select (blue background).</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the <code>&lt;select&gt;</code> element based on the element locator provided.</li>
        <li>Searches all options within the select tag.</li>
        <li>Selects the option whose <code>value</code> attribute matches the value provided.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Selecting a country by its code instead of visible text</li>
      <li>Choosing a predefined ID from a dropdown</li>
      <li>Any dropdown created using the HTML <code>&lt;select&gt;</code> tag</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>select-box-by-value</code>  
        <b>Element:</b> <code>#countryDropdown</code>  
        <b>Value:</b> <code>US</code>
      </li>
      <li>
        This will select the option with <code>value="US"</code> from the dropdown with ID <code>countryDropdown</code>.
      </li>
    </ul>
                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/select box by value.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        This action only works for dropdowns created using the HTML <code>&lt;select&gt;</code> tag.  
        Ensure the value matches exactly the <code>value</code> attribute of the option.
      </span>
    </div>
  </div>
`,
  'select-box-by-index': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">select-box-by-index</h1>

    <div class="main-section-title">Description</div>
    <p>
      Selects an option from a dropdown menu (created using the HTML <code>&lt;select&gt;</code> tag) based on its index position.  
      Indexing starts from <code>0</code> for the first option.  
      This action is only applicable to standard HTML select dropdowns.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">select-box-by-index</div>
      <div class="press-key-element">element</div>
      <div class="press-key-key">Index Number</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>select-box-by-index</b>) with maroon background.</li>
      <li><b>Second Part</b> – The dropdown element to select from (gray background). Use a proper locator like ID, name, or CSS selector.</li>
      <li><b>Third Part</b> – The index number of the option to select (blue background). Starts at 0 for the first option.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the <code>&lt;select&gt;</code> element based on the element locator provided.</li>
        <li>Accesses all options within the select tag.</li>
        <li>Selects the option at the index specified in the value section.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Selecting the first option in a dropdown (<code>Index 0</code>)</li>
      <li>Choosing options dynamically based on numeric position</li>
      <li>Any dropdown created using the HTML <code>&lt;select&gt;</code> tag</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>select-box-by-index</code>  
        <b>Element:</b> <code>#countryDropdown</code>  
        <b>Value:</b> <code>2</code>
      </li>
      <li>
        This will select the 3rd option (index 2) from the dropdown with ID <code>countryDropdown</code>.
      </li>
    </ul>
                                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/select box by index.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Only works for dropdowns created using the HTML <code>&lt;select&gt;</code> tag.  
        Make sure the index number is within the range of available options.
      </span>
    </div>
  </div>
`
};
export const utilityMap: { [key: string]: string } = {
  'compare-image-snippet': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">compare-image-snippet</h1>

    <div class="main-section-title">Description</div>
    <p>
      Compares a given image snippet against the specified element on the page.  
      If the element contains the image, a tick (✔) is shown; otherwise, it displays "The image did not match".  
      Useful for visual validations in automation testing.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">compare-image-snippet</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>compare-image-snippet</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element (gray background) containing the image to compare. Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Checks if the element contains the expected image snippet.</li>
        <li>If the image is found, a tick (✔) is shown; if not, the message "The image did not match" is displayed.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating visual elements on a webpage or application.</li>
      <li>Checking logos, buttons, icons, or images are correctly displayed.</li>
      <li>Automating UI/UX visual tests.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>compare-image-snippet</code>  
        <b>Element:</b> <code>#logo</code>
      </li>
      <li>Result: If the logo matches the expected snippet, a tick (✔) is shown; otherwise, "The image did not match".</li>

      <li>
        <b>Action:</b> <code>compare-image-snippet</code>  
        <b>Element:</b> <code>//div[@class='banner-image']</code>
      </li>
      <li>Result: If the banner image matches, ✔; otherwise, "The image did not match".</li>
    </ul>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        The action only verifies the presence of the expected image snippet visually.
      </span>
    </div>
  </div>
`, 'verify-image-snippet': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">verify-image-snippet</h1>

    <div class="main-section-title">Description</div>
    <p>
      Verifies that a given image snippet exists within the specified element on the page.  
      The step passes if the image matches and fails if the image does not match.  
      Useful for visual validations in automation testing.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">verify-image-snippet</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>verify-image-snippet</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element (gray background) containing the image to verify. Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Checks whether the element contains the expected image snippet.</li>
        <li>Passes the step if the image matches; fails if the image does not match.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Validating logos, icons, buttons, or other UI images.</li>
      <li>Ensuring correct images are displayed after certain actions.</li>
      <li>Automating visual UI verification scenarios.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>verify-image-snippet</code>  
        <b>Element:</b> <code>#logo</code>
      </li>
      <li>Result: Passes if the logo matches the expected snippet; otherwise fails.</li>

      <li>
        <b>Action:</b> <code>verify-image-snippet</code>  
        <b>Element:</b> <code>//div[@class='banner-image']</code>
      </li>
      <li>Result: Passes if the banner image matches the expected snippet; otherwise fails.</li>
    </ul>
                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/verify image snippet.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        This action verifies the image visually but does not validate other element properties.
      </span>
    </div>
  </div>
`, 'assert-image-snippet': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">assert-image-snippet</h1>

    <div class="main-section-title">Description</div>
    <p>
      Asserts that a given image snippet exists within the specified element on the page.  
      The test immediately fails if the image does not match the expected snippet.  
      Useful for strict visual validations in automation testing.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">assert-image-snippet</div>
      <div class="press-key-element">Element</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>assert-image-snippet</b>) with maroon background.</li>
      <li><b>Second Part</b> – The target element (gray background) containing the image to assert. Can be located using ID, CSS selector, XPath, or other locator.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Finds the specified element on the page.</li>
        <li>Compares the element’s image with the expected image snippet.</li>
        <li>Immediately fails the test if the image does not match; passes if it matches.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Strict validation of logos, icons, or UI graphics.</li>
      <li>Ensuring branding elements are correctly displayed.</li>
      <li>Automated visual regression testing scenarios.</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>
        <b>Action:</b> <code>assert-image-snippet</code>  
        <b>Element:</b> <code>#logo</code>
      </li>
      <li>Result: Passes if the logo matches the expected snippet; otherwise the test fails immediately.</li>

      <li>
        <b>Action:</b> <code>assert-image-snippet</code>  
        <b>Element:</b> <code>//div[@class='banner-image']</code>
      </li>
      <li>Result: Passes if the banner image matches; otherwise the test fails.</li>
    </ul>
                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/assert image snippet.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the element locator is correct and the element is visible.  
        This action performs strict validation and will stop execution if the image does not match.
      </span>
    </div>
  </div>
`
  ,
  'js-snippet': `
  <div>
    <h1 style="color: #a86b63; font-size: 2em; font-weight: 700;">
      js-snippet
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a custom JavaScript snippet in the context of the current page or session.  
      The JS code is entered in the value section. You can perform DOM manipulations, trigger events, or return values dynamically.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-action">js-snippet</div>
      <div class="press-key-key">// your JS code here</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>js-snippet</b>) with maroon background.</li>
      <li><b>Second Part</b> – The value section (blue background) where you type your JavaScript code.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Runs the JavaScript code provided in the value section.</li>
        <li>Executes it in the context of the current page or session.</li>
        <li>Allows manipulation of DOM elements, triggering events, or returning results for further actions.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Clicking elements or filling forms dynamically</li>
      <li>Performing calculations or logic not available via standard actions</li>
      <li>Injecting scripts, triggering events, or logging messages to the console</li>
    </ul>

    <div class="main-section-title">Example</div>
    <ul>
      <li>Log a message: <code>console.log('Hello from JS Snippet!');</code></li>
      <li>Click a button dynamically: <code>document.querySelector('#submitBtn').click();</code></li>
      <li>Return a value: <code>var text = document.querySelector('#username').value;</code></li>
    </ul>
                                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_Commands/js snippet.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure your JavaScript code is valid.  
        You can also store results in variables if needed for further automation.  
        Example: <code>##myResult = document.querySelector('#value').textContent;</code>
      </span>
    </div>
  </div>
`
};
export const apiMap: { [key: string]: string } = {
  'get-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      get-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Execute a GET API request on the specified API object and store the response in a variable. Once this suite is executed and a success report is generated, you can later click on the value section to view the full API response.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">get-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>get-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store API response (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the GET API using the provided API object.</li>
        <li>Stores the full API response into the specified variable.</li>
        <li>Allows viewing the response by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Fetching data from REST APIs for automation workflows</li>
      <li>Storing and using API responses dynamically</li>
      <li>Testing endpoints imported from Postman or other API tools</li>
    </ul>
                                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/get api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
  </video>
</div>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response.  
        Example: <code>##apiResponse</code>.
      </span>
    </div>
  </div>
`  ,
  'post-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      post-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a POST API request on the specified API object and stores the response in a variable.  
      Once executed successfully, you can click on the value section to view the full API response.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">post-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>post-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store API response (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the POST API using the provided API object.</li>
        <li>Stores the full API response into the specified variable.</li>
        <li>Allows viewing the response by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Sending data to REST APIs for automation workflows</li>
      <li>Storing and using API responses dynamically</li>
      <li>Testing POST endpoints imported from Postman or other tools</li>
    </ul>
                                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/post api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response.  
        Example: <code>##apiResponse</code>.
      </span>
    </div>
  </div>
`  ,
  'put-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      put-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a PUT API request on the specified API object and stores the response in a variable.  
      Once executed successfully, you can click on the value section to view the full API response.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">put-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>put-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store API response (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the PUT API using the provided API object.</li>
        <li>Stores the full API response into the specified variable.</li>
        <li>Allows viewing the response by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Updating data via REST APIs for automation workflows</li>
      <li>Storing and using API responses dynamically</li>
      <li>Testing PUT endpoints imported from Postman or other tools</li>
    </ul>
                                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/put api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response.  
        Example: <code>##apiResponse</code>.
      </span>
    </div>
  </div>
`  ,
  'patch-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      patch-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a PATCH API request on the specified API object and stores the response in a variable.  
      Once executed successfully, you can click on the value section to view the full API response.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">patch-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>patch-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store API response (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the PATCH API using the provided API object.</li>
        <li>Stores the full API response into the specified variable.</li>
        <li>Allows viewing the response by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Updating partial data via REST APIs for automation workflows</li>
      <li>Storing and using API responses dynamically</li>
      <li>Testing PATCH endpoints imported from Postman or other tools</li>
    </ul>
                                                                 <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/patch api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response.  
        Example: <code>##apiResponse</code>.
      </span>
    </div>
  </div>
`  ,
  'delete-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      delete-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a DELETE API request on the specified API object and stores the response in a variable.  
      Once executed successfully, you can click on the value section to view the full API response.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">delete-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>delete-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store API response (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the DELETE API using the provided API object.</li>
        <li>Stores the full API response into the specified variable.</li>
        <li>Allows viewing the response by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Deleting data via REST APIs for automation workflows</li>
      <li>Storing and using API responses dynamically</li>
      <li>Testing DELETE endpoints imported from Postman or other tools</li>
    </ul>
                                                                     <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/delete api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response.  
        Example: <code>##apiResponse</code>.
      </span>
    </div>
  </div>
`  ,
  'head-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      head-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes a HEAD API request on the specified API object and stores the response headers in a variable.  
      Once executed successfully, you can click on the value section to view the full response headers.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">head-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>head-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store response headers (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response headers.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the HEAD API using the provided API object.</li>
        <li>Stores only the response headers into the specified variable.</li>
        <li>Allows viewing the headers by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Checking if a resource exists without downloading its content</li>
      <li>Fetching headers for verification or validation in automation workflows</li>
      <li>Testing HEAD endpoints imported from Postman or other tools</li>
    </ul>
                                                                         <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/head api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response headers.  
        Example: <code>##apiResponseHeaders</code>.
      </span>
    </div>
  </div>
`,
  'options-api': `
  <div>
    <h1 style="color: #9372c2; font-size: 2em; font-weight: 700;">
      options-api
    </h1>

    <div class="main-section-title">Description</div>
    <p>
      Executes an OPTIONS API request on the specified API object and stores the response headers in a variable.  
      Once executed successfully, you can click on the value section to view the full response headers and allowed HTTP methods.
    </p>

    <div class="main-section-title">Structure</div>
    <div class="press-key-structure">
      <div class="press-key-api">options-api</div>
      <div class="press-key-element">api-object</div>
      <div class="press-key-key">##myResponse</div>
    </div>
    <ul>
      <li><b>First Part</b> – The action (<b>options-api</b>) with background color <code>purple</code>.</li>
      <li><b>Second Part</b> – The API object to call (gray background). Can be manually created or imported from Postman or other tools.</li>
      <li><b>Third Part</b> – Variable to store response headers and allowed methods (blue background). Example: <code>##myResponse</code>. Click this section after execution to view the response.</li>
    </ul>

    <div class="main-section-title">How It Works</div>
    <p>
      When executed, the system:
      <ul>
        <li>Calls the OPTIONS API using the provided API object.</li>
        <li>Stores the response headers and allowed HTTP methods into the specified variable.</li>
        <li>Allows viewing the headers and methods by clicking the variable value section in the UI.</li>
      </ul>
    </p>

    <div class="main-section-title">Use Case</div>
    <ul>
      <li>Checking allowed HTTP methods on a resource</li>
      <li>Verifying server support for specific actions before executing requests</li>
      <li>Testing OPTIONS endpoints imported from Postman or other tools</li>
    </ul>
                                                                             <div style="margin: 10px 0 24px 0;">
  <video
    src="/assets/Guide Video/Action_API_Commands/options api.mp4"
    autoplay
    muted
    loop
    playsinline
    style="width: 100%; object-fit: cover; border: 1px solid #22262b;
    border-radius: 15px;">
 </video>

    <div class="blue-note">
      <span class="icon">&#9432;</span>
      <strong>Note:</strong>
      <span>
        Ensure the API object is valid and accessible.  
        Use a variable name starting with <code>##</code> to store the response headers.  
        Example: <code>##apiOptionsResponse</code>.
      </span>
    </div>
  </div>
`

};